// src/components/tier/TierData.js
import React, { useCallback, useState, useEffect, useMemo, useRef, useDeferredValue } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import styled from "styled-components";
import {
  FiSearch,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiStar,
  FiChevronUp
} from "react-icons/fi";
import Tilt from "react-parallax-tilt";
import Select from "react-select";
import { analyzeTeamByAI, askCoachByAI } from "../../utils/aiApi";
import { BRAND_COLORS, TEAM_SHORT } from "../../theme/brandTheme";

// ✅ ใช้ Competitor_Hero_stats ก่อน แล้วค่อย fallback ไปชีททีม
import {
  parseOpponentCombosForTeam,
  normalizeOppToComboStats,
  parseWorkbookToComboStats,
  parseRplStatsTeamsP,
  parseFsOneHeroFromRplPlayerStatsP,
  parseSpecterScrimP
} from "../../utils/excelParser.js";
// เกมของทีมคู่แข่งที่ "ส่องมา" (ชีท Specter_Scrim) — ไม่มี FS อยู่ในนั้น
import { specterGamesForTeam } from "../../utils/tactic/teamScout";
import {
  canonicalHeroName,
  getHeroImageUrlWithFallback,
  heroBlockKey
} from "../../utils/heroCatalog";
import {
  buildHeroMatchups,
  buildCounterPicks,
  normalizeRole,
  rolesFromComboLabel,
} from "../../utils/heroMatchups";
import { useSeasonLog } from "./useSeasonLog";
import {
  WINDOW_CHOICES,
  filterMatchupPool,
  gameTypesInPool,
} from "../../utils/matchupFilters";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const theme = {
  main: BRAND_COLORS.primary,
  accent: BRAND_COLORS.primaryDark,
  green: BRAND_COLORS.success,
  card: "rgba(255,255,255,0.96)",
  cardGlass: "rgba(255,255,255,0.90)",
  surface: "#ffffff",
  surfaceStrong: "rgba(255,255,255,0.97)",
  bg: BRAND_COLORS.bg,
  font: "'Kanit', system-ui, -apple-system, 'Segoe UI', sans-serif",
  gold: "#FFD700",
  silver: "#B3B3B3",
  bronze: "#E5A76B",
  neon: BRAND_COLORS.primary,
  white: BRAND_COLORS.text,
  text: BRAND_COLORS.text,
  secondary: BRAND_COLORS.textMuted,
  success: BRAND_COLORS.success,
  danger: BRAND_COLORS.danger,
  warn: BRAND_COLORS.warn,
  blue: BRAND_COLORS.primaryLight,
  border: BRAND_COLORS.border,
  borderSoft: BRAND_COLORS.borderSoft,
  tintXs: "rgba(255,16,38,0.03)",
  tintSm: "rgba(255,16,38,0.06)",
  tintMd: "rgba(255,16,38,0.08)",
  tintLg: "rgba(255,16,38,0.10)",
};

const Bg = styled.div`
  min-height: 100vh;
  font-family: ${theme.font};
  background: ${theme.bg};
  position: relative;
  color: ${theme.text};
  overflow-x: hidden;
`;

/* ---------- Tabs: viewport + arrows ---------- */
const TabsWrap = styled.div`
  position: relative;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto 28px;
`;
const TabsViewport = styled.div`
  background: rgba(255, 255, 255, 0.92);
  border-radius: 26px;
  box-shadow: 0 8px 22px rgba(255,16,38,0.10);
  backdrop-filter: blur(10px);
  overflow: hidden;
  border: 1px solid rgba(255,16,38,0.2);
`;
const TabsScroller = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  padding: 10px 64px; /* เว้นที่ให้ปุ่มลูกศร */
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar { display: none; } /* Chrome/Safari */
`;
const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 900;
  color: #fff;
  background: linear-gradient(135deg, ${theme.main}, ${theme.accent});
  box-shadow: 0 0 15px rgba(255,16,38,0.4);
  display: ${({ show }) => (show ? "grid" : "none")};
  place-items: center;
  z-index: 2;
  opacity: .95;
`;
const ArrowLeft = styled(ArrowBtn)` left: 10px; `;
const ArrowRight = styled(ArrowBtn)` right: 10px; `;
const EdgeFade = styled.div`
  pointer-events: none;
  position: absolute; top: 0; bottom: 0; width: 56px;
  ${({ left }) => left ? "left: 0;" : "right: 0;"}
  background: ${({ left }) =>
    left
      ? "linear-gradient(90deg, #fff6f7 0%, transparent 100%)"
      : "linear-gradient(270deg, #fff6f7 0%, transparent 100%)"};
  border-radius: 26px;
  display: ${({ show }) => (show ? "block" : "none")};
`;

/* ---------- tiny utils (safe) ---------- */
const asArray = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]).flat();
const isNum = (v) => Number.isFinite(Number(v));
const fmtNum = (v, digits = 2) => (isNum(v) ? Number(v).toFixed(digits) : "-");
const fmtInt = (v) => (isNum(v) ? Math.round(Number(v)).toLocaleString() : "-");
const fmtPercent = (v, digits = 1) => (isNum(v) ? `${Number(v).toFixed(digits)}%` : "-");
const safeHeroKey = (s) => heroBlockKey(s);
const TIER_AI_QUICK_PROMPTS = [
  "สรุปภาพรวมคอมโบที่ควรโฟกัสตอนนี้",
  "คอมโบไหนควรลด priority ก่อนแข่ง",
  "ช่วยสรุปแผนซ้อมสั้นๆ 3 ข้อ",
];

function buildTierFocusLabel(comboType) {
  if (!comboType || comboType === "all") return "all-combos";
  if (comboType === "1-hero") return "one-hero-only";
  if (String(comboType).startsWith("2-heroes")) return "two-heroes-only";
  if (String(comboType).startsWith("3-heroes")) return "three-heroes-only";
  if (comboType === "5-heroes") return "five-heroes-only";
  return "filtered-combos";
}

/* ---------- styled for grid/skeleton ---------- */
const ComboGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin: 38px auto 48px;
  max-width: 1200px;

  /* กัน Layout Shift เวลาดาต้าขึ้นลง */
  min-height: 520px;

  @media (max-width: 700px) {
    gap: 15px;
  }
`;

const GlobalPulse = styled.div`
  @keyframes pulse {
    0% { opacity: .65; }
    50% { opacity: .95; }
    100% { opacity: .65; }
  }
`;

const SkeletonCard = () => (
  <div
    style={{
      background: "rgba(255,255,255,0.05)",
      borderRadius: 24,
      height: 220,
      border: "1px solid rgba(255,16,38,0.1)",
      boxShadow: "0 8px 18px rgba(255,16,38,0.12)",
      animation: "pulse 1.5s ease-in-out infinite",
    }}
  />
);

const TabBtn = styled.button`
  border: none;
  outline: none;
  cursor: pointer;
  background: ${({ active }) =>
    active
      ? `linear-gradient(135deg,${theme.main},${theme.accent})`
      : "transparent"};
  color: ${({ active }) => (active ? "#fff" : theme.main)};
  font-weight: 900;
  font-size: 1.12rem;
  padding: 11px 34px;
  border-radius: 21px;
  transition: background 0.19s, color 0.17s;
  white-space: nowrap;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 0 auto 22px;
  padding: 15px 18px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255,16,38,0.2);
  border-radius: 20px;
  box-shadow: 0 0 20px rgba(255,16,38,0.1);
  width: 90%;
  max-width: 1120px;
`;

const FilterLabel = styled.div`
  font-weight: 900;
  color: ${theme.main};
  margin-bottom: 7px;
  font-size: 1.05rem;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 140px;
`;

const BtnGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const FilterBtn = styled.button`
  background: ${({ active }) =>
    active
      ? `linear-gradient(135deg, ${theme.main}, ${theme.accent})`
      : theme.tintSm};
  color: ${({ active }) => (active ? "#fff" : theme.secondary)};
  font-weight: 900;
  font-size: 1.04rem;
  border: 1px solid ${({ active }) => active ? "transparent" : theme.borderSoft};
  border-radius: 13px;
  padding: 10px 19px;
  cursor: pointer;
  transition: all 0.16s;
  &:hover {
    background: linear-gradient(135deg, ${theme.main}cc, ${theme.accent}cc);
    color: #fff;
  }
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.96);
  border: 1px solid rgba(255,16,38,0.3);
  border-radius: 13px;
  padding: 0 7px;
  flex: 1;
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  font-size: 1.18rem;
  color: ${theme.text};
  font-weight: 900;
  padding: 12px;
  &::placeholder {
    color: rgba(107,114,128,0.7);
  }
`;

const ComboCard = styled.div`
  background: rgba(255,255,255,0.96);
  border-radius: 24px;
  border: 1px solid rgba(255,16,38,0.2);
  box-shadow: 0 10px 28px rgba(255,16,38,0.12);
  padding: 30px 19px 22px;
  text-align: center;
  position: relative;
  transition: all 0.25s;
  &:hover {
    box-shadow: 0 0 30px rgba(255,16,38,0.2);
    border-color: ${theme.main};
    transform: translateY(-5px);
  }
`;

const HeroRow = styled.div`
  display: flex;
  gap: 9px;
  justify-content: center;
  margin-bottom: 14px;
`;

const NeonHero = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  overflow: hidden;
  background: #fff5f7;
  border: 2px solid ${theme.main};
  box-shadow: 0 0 10px ${theme.main}40;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ComboTitle = styled.div`
  font-size: 1.22rem;
  font-weight: 900;
  color: ${theme.main};
  margin-bottom: 5px;
`;

const ComboDetail = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 6px;
  font-weight: 800;
  font-size: 1.12rem;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 16px;
`;

const ShineBtn = styled.button`
  background: linear-gradient(135deg, ${theme.main}, ${theme.accent});
  color: #fff;
  font-weight: 900;
  border: none;
  border-radius: 13px;
  padding: 11px 20px;
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  transition: all 0.21s;
  &:hover {
    box-shadow: 0 0 20px ${theme.main}60;
    transform: translateY(-2px);
  }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(255,245,247,0.92);
  display: flex; align-items: center; justify-content: center;
  z-index: 199;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 28px;
  padding: 32px 18px 26px;
  max-width: 500px; width: 96vw; max-height: 94vh;
  overflow-y: auto; border: 1px solid ${theme.main};
  box-shadow: 0 0 50px rgba(255,16,38,0.2);
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute; top: 17px; right: 22px;
  font-size: 2rem; color: ${theme.gold};
  background: none; border: none; cursor: pointer;
`;

const Toast = styled.div`
  position: fixed; bottom: 3.3rem; left: 50%;
  transform: translateX(-50%);
  background: ${theme.main}; color: #fff;
  padding: 17px 32px; border-radius: 13px;
  font-weight: 900; z-index: 1020;
`;

/* ---------- Matchup panel (ในโมดัล) ---------- */
const MatchupTab = styled.button`
  flex: 1;
  background: ${({ $active }) =>
    $active ? `linear-gradient(135deg, ${theme.main}, ${theme.accent})` : "rgba(255,255,255,0.05)"};
  color: ${({ $active }) => ($active ? "#fff" : theme.secondary)};
  border: 1px solid ${({ $active }) => ($active ? "transparent" : "rgba(255,255,255,0.12)")};
  border-radius: 11px;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.86rem;
  padding: 8px 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.16s;
  &:hover { color: #fff; border-color: ${theme.main}; }
`;

const MinGamesChip = styled.button`
  background: ${({ $active }) => ($active ? theme.main : "rgba(255,255,255,0.05)")};
  color: ${({ $active }) => ($active ? "#fff" : theme.secondary)};
  border: 1px solid ${({ $active }) => ($active ? "transparent" : "rgba(255,255,255,0.12)")};
  border-radius: 8px;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.78rem;
  padding: 5px 10px;
  cursor: pointer;
  &:hover { color: #fff; border-color: ${theme.main}; }
`;

const MatchupRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px;
  border-radius: 10px;
  background: rgba(255,255,255,0.04);
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  outline: ${({ $open }) => ($open ? `1px solid ${theme.main}` : "none")};
  transition: outline 0.15s;
  &:hover {
    background: ${({ $clickable }) => ($clickable ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)")};
  }
`;

/* แถบพื้นหลังบอกสัดส่วนจำนวนเกม อ่านเร็วกว่าไล่ตัวเลข */
const MatchupBar = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => Math.max(2, Math.min(100, $pct))}%;
  background: linear-gradient(90deg, ${theme.main}33, ${theme.main}0d);
  pointer-events: none;
`;

const ScrollTopBtn = styled.button`
  position: fixed; bottom: 35px; right: 30px;
  width: 56px; height: 56px; border-radius: 50%;
  background: linear-gradient(135deg, ${theme.main},${theme.accent});
  color: #fff; font-size: 1.6rem; border: none; cursor: pointer;
`;

/* ===================== Utilities ===================== */

// ตำแหน่งใช้ตัวเดียวกับที่ heroMatchups ใช้ ไม่ให้การจับคู่ role เพี้ยนกันคนละที่
const normalizePosition = normalizeRole;

const renderHeroImage = (src, alt, size = 64) => (
  <img
    src={src}
    alt={alt}
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      objectFit: "cover",
      background: theme.bg,
      border: `2px solid ${theme.main}`
    }}
    onError={(e) => {
      if (e.currentTarget.dataset.fallback) return;
      e.currentTarget.dataset.fallback = "1";
      e.currentTarget.onerror = null;
      e.currentTarget.src = `${process.env.PUBLIC_URL}/heroimages/default.jpg`;
    }}
  />
);

function buildWeekMap(dates) {
  if (!dates || !dates.length) return {};
  const sorted = [...dates].sort();
  const weekMap = {};
  let weekNum = 1;
  for (let i = 0; i < sorted.length; ++i) {
    const d = sorted[i];
    weekMap[d] = "W" + weekNum;
    if ((i + 1) % 7 === 0) weekNum++;
  }
  return weekMap;
}

function getHeroImage(heroName, role = "") {
  return getHeroImageUrlWithFallback(heroName, role);
}

const FS_ROLE_METRIC_COLS = {
  DSL: { kill: 76, death: 77, assist: 78, gpm: 117, dmgMin: 141 },
  JUG: { kill: 79, death: 80, assist: 81, gpm: 118, dmgMin: 142 },
  MID: { kill: 82, death: 83, assist: 84, gpm: 119, dmgMin: 143 },
  ADL: { kill: 85, death: 86, assist: 87, gpm: 120, dmgMin: 144 },
  SUP: { kill: 88, death: 89, assist: 90, gpm: 121, dmgMin: 145 },
};

function numOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatMetric(v, digits = 1) {
  return Number.isFinite(v) ? Number(v).toFixed(digits) : "-";
}

function buildPairComboList(gamesArr, roleA, roleB, label) {
  const acc = new Map();
  gamesArr.forEach((g) => {
    const picks = (g.fsPick || []).map(canonicalHeroName);
    const positions = (g.fsPosition || []).map(normalizePosition);
    const aIdx = positions.findIndex((p) => p === roleA);
    const bIdx = positions.findIndex((p) => p === roleB);
    if (aIdx === -1 || bIdx === -1) return;
    const hA = picks[aIdx], hB = picks[bIdx];
    if (!hA || !hB) return;
    const comboName = `${hA} + ${hB}`;
    const slot = acc.get(comboName) || { win: 0, total: 0 };
    slot.total += 1;
    if (g.win === 1 || g.result === "Win" || g.win === "1") slot.win += 1;
    acc.set(comboName, slot);
  });
  return Array.from(acc.entries()).map(([name, v]) => {
    const total = v.total || 0;
    const wins = v.win || 0;
    const losses = Math.max(0, total - wins);
    const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
    const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;
    const chars = name.split(" + ").map((s) => s.trim());
    return {
      name,
      comboType: label,
      characters: chars,
      images: chars.map((h, i) => getHeroImage(h, [roleA, roleB][i])),
      wins,
      totalGames: total,
      losses,
      winRate,
      loseRate
    };
  });
}

/* NEW: สร้างคอมโบ 3 ตัวจาก roles เฉพาะที่ต้องการ */
function buildTripleComboList(gamesArr, roleA, roleB, roleC, label) {
  const acc = new Map();
  gamesArr.forEach((g) => {
    const picks = (g.fsPick || []).map(canonicalHeroName);
    const positions = (g.fsPosition || []).map(normalizePosition);
    const aIdx = positions.findIndex((p) => p === roleA);
    const bIdx = positions.findIndex((p) => p === roleB);
    const cIdx = positions.findIndex((p) => p === roleC);
    if (aIdx === -1 || bIdx === -1 || cIdx === -1) return;
    const hA = picks[aIdx], hB = picks[bIdx], hC = picks[cIdx];
    if (!hA || !hB || !hC) return;
    const comboName = [hA, hB, hC].join(" + ");
    const slot = acc.get(comboName) || { win: 0, total: 0 };
    slot.total += 1;
    if (g.win === 1 || g.result === "Win" || g.win === "1") slot.win += 1;
    acc.set(comboName, slot);
  });
  return Array.from(acc.entries()).map(([name, v]) => {
    const total = v.total || 0;
    const wins = v.win || 0;
    const losses = Math.max(0, total - wins);
    const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
    const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;
    const chars = name.split(" + ").map((s) => s.trim());
    return {
      name,
      comboType: label,
      characters: chars,
      images: chars.map((h, i) => getHeroImage(h, [roleA, roleB, roleC][i])),
      wins,
      totalGames: total,
      losses,
      winRate,
      loseRate
    };
  });
}

function isFsWin(g) {
  return g.win === 1 || g.win === "1" || String(g.result || "").toLowerCase() === "win";
}

function normalizeTeamCode(t) {
  const s = String(t || "").trim().toUpperCase();
  if (s === "TLN") return "TEN";
  if (s === "EAG") return "EA";
  if (s === "HDG") return "HD";
  return s;
}

function buildOneHeroListBySide(gamesArr, side = "fs") {
  const acc = new Map();
  for (const g of gamesArr) {
    const picks = side === "op" ? (g.opPick || []) : (g.fsPick || []);
    const positions = (side === "op" ? (g.opPosition || []) : (g.fsPosition || [])).map(normalizePosition);
    const winFlag = side === "op" ? !isFsWin(g) : isFsWin(g);
    for (let i = 0; i < picks.length; i++) {
      const hero = String(picks[i] || "").trim();
      const pos = String(positions[i] || "").trim();
      if (!hero || !pos) continue;
      const key = `${hero}|||${pos}`;
      const slot = acc.get(key) || { hero, position: pos, total: 0, win: 0 };
      slot.total += 1;
      if (winFlag) slot.win += 1;
      acc.set(key, slot);
    }
  }
  return [...acc.values()].map((x) => ({
    hero: x.hero,
    position: x.position,
    total: x.total,
    win: x.win,
    winrate: x.total ? Number(((x.win / x.total) * 100).toFixed(1)) : 0
  }));
}

function buildPairComboListBySide(gamesArr, roleA, roleB, label, side = "fs") {
  const acc = new Map();
  for (const g of gamesArr) {
    const picks = side === "op" ? (g.opPick || []) : (g.fsPick || []);
    const positions = (side === "op" ? (g.opPosition || []) : (g.fsPosition || [])).map(normalizePosition);
    const aIdx = positions.findIndex((p) => p === roleA);
    const bIdx = positions.findIndex((p) => p === roleB);
    if (aIdx === -1 || bIdx === -1) continue;
    const a = String(picks[aIdx] || "").trim();
    const b = String(picks[bIdx] || "").trim();
    if (!a || !b) continue;
    const combo = `${a} + ${b}`;
    const slot = acc.get(combo) || { win: 0, total: 0 };
    slot.total += 1;
    if (side === "op" ? !isFsWin(g) : isFsWin(g)) slot.win += 1;
    acc.set(combo, slot);
  }
  return [...acc.entries()].map(([combo, v]) => ({
    combo,
    win: v.win,
    total: v.total,
    losses: Math.max(0, v.total - v.win),
    comboType: label
  }));
}

function buildTripleComboListBySide(gamesArr, roleA, roleB, roleC, label, side = "fs") {
  const acc = new Map();
  for (const g of gamesArr) {
    const picks = side === "op" ? (g.opPick || []) : (g.fsPick || []);
    const positions = (side === "op" ? (g.opPosition || []) : (g.fsPosition || [])).map(normalizePosition);
    const aIdx = positions.findIndex((p) => p === roleA);
    const bIdx = positions.findIndex((p) => p === roleB);
    const cIdx = positions.findIndex((p) => p === roleC);
    if (aIdx === -1 || bIdx === -1 || cIdx === -1) continue;
    const a = String(picks[aIdx] || "").trim();
    const b = String(picks[bIdx] || "").trim();
    const c = String(picks[cIdx] || "").trim();
    if (!a || !b || !c) continue;
    const combo = `${a} + ${b} + ${c}`;
    const slot = acc.get(combo) || { win: 0, total: 0 };
    slot.total += 1;
    if (side === "op" ? !isFsWin(g) : isFsWin(g)) slot.win += 1;
    acc.set(combo, slot);
  }
  return [...acc.entries()].map(([combo, v]) => ({
    combo,
    win: v.win,
    total: v.total,
    losses: Math.max(0, v.total - v.win),
    comboType: label
  }));
}

/* Map label ให้ครอบทั้ง 2 และ 3 ตัว */
function normalizeComboTypeLabel(t) {
  const s = String(t || "").toUpperCase();
  // 3-heroes
  const hasSup = s.includes("SUP") || s.includes("ROAM");
  const hasJug = s.includes("JUG") || s.includes("JUNGLE");
  if (hasJug && s.includes("MID") && hasSup) return "3-heroes (JUG+MID+SUP)";
  if (hasJug && s.includes("ADL") && hasSup) return "3-heroes (JUG+ADL+SUP)";
  if (s.includes("MID") && s.includes("ADL") && hasSup) return "3-heroes (MID+ADL+SUP)";
  // 2-heroes
  if (s.includes("MID") && hasSup) return "2-heroes (MID+SUP)";
  if (s.includes("ADL") && hasSup) return "2-heroes (ADL+SUP)";
  if (s.includes("DSL") && hasJug) return "2-heroes (DSL+Jungle)";
  if (hasJug && hasSup) return "2-heroes (JUG+SUP)";
  if (hasJug && s.includes("MID")) return "2-heroes (JUG+MID)";
  return String(t || "");
}

function comboTypeMatches(activeType, comboTypeValue) {
  if (activeType === "all") return true;
  if (!activeType) return true;
  const normalized = normalizeComboTypeLabel(comboTypeValue);
  if (activeType === "1-hero") return normalized === "1-hero";
  return normalized === activeType;
}

/* Build 5-heroes combos from matches */
function buildFiveCombosFromGames(gamesArr) {
  const acc = new Map();
  for (const g of gamesArr) {
    const picks = (g.fsPick || []).map(canonicalHeroName).filter(Boolean);
    if (picks.length < 5) continue;
    const chars = [...new Set(picks)].slice(0, 5).sort((a, b) => a.localeCompare(b));
    const key = chars.join(" + ");
    const v = acc.get(key) || { wins: 0, games: 0, lastUsed: g.Date || null };
    v.games += 1;
    if (g.win === 1 || g.win === "1" || String(g.result).toLowerCase() === "win") v.wins += 1;
    const d = g.Date || g.date;
    if (d && (!v.lastUsed || String(d) > String(v.lastUsed))) v.lastUsed = d;
    acc.set(key, v);
  }
  return Array.from(acc.entries()).map(([k, v]) => {
    const chars = k.split(" + ");
    const wins = v.wins, total = v.games, losses = Math.max(0, total - wins);
    const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
    const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;
    return {
      name: k,
      comboType: "5-heroes",
      characters: chars,
      images: chars.map(getHeroImage),
      wins, totalGames: total, losses, winRate, loseRate,
      lastUsed: v.lastUsed || null
    };
  });
}

/* ===================== Matchup panel =====================
   "ตัวนี้/คอมนี้ เจอตัวไหนบ่อย แล้ววินเรทเท่าไหร่" — อ่านจาก log เกมต่อเกมเท่านั้น
   games: เกมที่กรองช่วงเวลา/ทีมมาแล้ว | combo: การ์ดที่กด | side: 'us' | 'them' */
export function HeroMatchupPanel({
  games,
  combo,
  side = "us",
  opponentLabel = "",
  // Specter_Scrim rows for this opponent, already in game shape with them on
  // the "op" side (see specterGamesForTeam). Their side only.
  scouted = [],
  source = "both",
  onSourceChange,
  heroImageOf,
  scope = "current",
  onScopeChange,
  scopeCounts,
  scopeLoading = false,
  seasonGames,
}) {
  const [view, setView] = useState("lane");
  const [expanded, setExpanded] = useState(false);
  const [minGamesRow, setMinGamesRow] = useState(1);
  const [windowDays, setWindowDays] = useState(0);
  const [gameType, setGameType] = useState("all");
  const [openRow, setOpenRow] = useState(null);
  const [exporting, setExporting] = useState("");
  const panelRef = useRef(null);

  const isOneHero = combo?.comboType === "1-hero";

  // ประเภทที่มีให้เลือก อ่านจากกองเต็มก่อนตัดวัน ไม่ให้ chip หายไปเพราะช่วงแคบ
  const poolTypes = useMemo(() => gameTypesInPool(games), [games]);
  // ...แต่ "รายชื่อ" ลีกอ่านจากกองที่กว้างที่สุดที่รู้จัก: ชีทของช่วงนี้มักคีย์ Type
  // เดียว (Scrim) แถวเลือกลีกเลยหายไปทั้งแถว ทั้งที่ log ทั้งซีซั่นมี RPL / s.Taiwan /
  // Coda อยู่ กดอันที่กองนี้ไม่มี = สลับไปกองทั้งซีซั่นให้เอง จะได้ไม่เจอตารางว่างเปล่า
  const seasonTypes = useMemo(() => gameTypesInPool(seasonGames), [seasonGames]);
  const typeChoices = useMemo(() => {
    if (!seasonTypes.length) return poolTypes.map((t) => ({ ...t, inPool: true }));
    const here = new Map(poolTypes.map((t) => [t.key, t.count]));
    return seasonTypes.map((t) => ({
      ...t,
      count: here.has(t.key) ? here.get(t.key) : t.count,
      inPool: here.has(t.key),
    }));
  }, [poolTypes, seasonTypes]);
  // Which sheet the opponent's numbers come from, before the day/type filters.
  const hasScouted = side === "them" && scouted.length > 0;
  const sourcePool = useMemo(() => {
    if (!hasScouted) return games;
    if (source === "us") return games;
    if (source === "scouted") return scouted;
    return [...(games || []), ...scouted];
  }, [hasScouted, source, games, scouted]);

  const pool = useMemo(
    () => filterMatchupPool(sourcePool, { days: windowDays, type: gameType }),
    [sourcePool, windowDays, gameType]
  );

  const matchup = useMemo(() => {
    const heroes = asArray(combo?.characters).map(canonicalHeroName).filter(Boolean);
    if (!heroes.length) return null;
    // การ์ดผูกกับตำแหน่ง: 1 ตัวใช้ position ของการ์ด (Violet(ADL)) ส่วนคอมโบใช้ตำแหน่ง
    // ในป้าย (2-heroes (ADL+SUP)) ไม่งั้นจำนวนเกมในแผงจะเกินการ์ดเวลาสลับเลนกัน
    const labelRoles = rolesFromComboLabel(combo?.comboType);
    const roles = isOneHero
      ? [normalizePosition(combo?.position)]
      : (labelRoles.length === heroes.length ? labelRoles : []);
    return buildHeroMatchups(pool, {
      members: heroes.map((hero, i) => ({ hero, role: roles[i] || "" })),
      side,
    });
  }, [pool, combo, side, isOneHero]);

  useEffect(() => { setExpanded(false); setOpenRow(null); }, [combo, view, minGamesRow]);
  useEffect(() => { setOpenRow(null); }, [games, scope, windowDays, gameType, source]);
  // ประเภทที่เลือกไว้อาจไม่มีในไฟล์/ช่วงใหม่ — กลับไปทั้งหมดแทนที่จะโชว์ตารางว่าง
  useEffect(() => {
    if (gameType === "all") return;
    if (!typeChoices.some((t) => t.key === gameType)) setGameType("all");
  }, [typeChoices, gameType]);

  const canScope = typeof onScopeChange === "function";
  const activeScope = canScope ? scope : "current";

  const pickType = (t) => {
    setGameType(t.key);
    if (!t.inPool && canScope) onScopeChange("season");
  };

  const toggleRow = (row) => setOpenRow((cur) => (cur?.name === row.name ? null : row));

  // กดแถวศัตรู = ถามกลับว่า "เจอตัวนี้แล้วเราหยิบอะไรตอบ"
  const counter = useMemo(() => {
    if (!openRow || view === "teams") return null;
    return buildCounterPicks(pool, { enemy: openRow.name, role: openRow.role, side });
  }, [openRow, pool, side, view]);

  const allRows =
    (view === "enemies" ? matchup?.enemies : view === "teams" ? matchup?.teams : matchup?.lane) || [];
  // เจอกันเกมเดียวบอกอะไรไม่ได้ ยิ่งโหมดทั้งซีซั่นยิ่งมีแถวแบบนั้นเยอะ
  const rows = allRows.filter((r) => r.games >= minGamesRow);
  const hiddenByMin = allRows.length - rows.length;
  const visibleRows = expanded ? rows : rows.slice(0, 8);
  const topGames = rows[0]?.games || 0;

  const sourceText = !hasScouted
    ? ""
    : source === "us"
    ? " · เกมกับเรา"
    : source === "scouted"
    ? " · ที่ส่องมา"
    : " · รวมสองชีท";
  const scopeText =
    (activeScope === "season" ? (windowDays ? `${windowDays} วันล่าสุด` : "ทั้งซีซั่น") : "ช่วงนี้") +
    sourceText +
    (gameType === "all" ? "" : ` · ${typeChoices.find((t) => t.key === gameType)?.type || gameType}`);
  const viewText = view === "enemies" ? "ศัตรูทั้ง 5" : view === "teams" ? "แยกตามทีม" : "คู่เลนเดียวกัน";
  const cardText = `${combo?.name || ""}${isOneHero && combo?.position ? `(${combo.position})` : ""}`;
  const exportTitle = `${cardText} — เจอตัวไหนบ่อย · ${viewText} · ${scopeText}` +
    (matchup?.totalGames ? ` · ${matchup.totalGames} เกม ${matchup.wins}-${matchup.losses} (${matchup.winRate}%)` : "");
  const cardSlug =
    (cardText.match(/[A-Za-z0-9]+/g) || []).join("-") || "card";
  const fileStem = `matchup_${cardSlug}_${view}_${activeScope}${windowDays ? `-${windowDays}d` : ""}${
    gameType === "all" ? "" : `_${gameType.replace(/[^a-z0-9]+/gi, "")}`
  }`;

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportCsv = () => {
    const head = ["ตัวที่เจอ", "ตำแหน่ง", "เกม", "ชนะ", "แพ้", "วินเรท%"];
    const body = rows.map((r) => [r.name, r.role || "", r.games, r.wins, r.losses, r.winRate]);
    const csv = [[exportTitle], [], head, ...body]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    saveBlob(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }), `${fileStem}.csv`);
  };

  // รูปไว้ส่งในกลุ่มทีม — กางรายการให้ครบก่อนถ่าย แล้วค่อยคืนสภาพเดิม
  const exportPng = async () => {
    const node = panelRef.current;
    if (!node || !rows.length) return;
    const wasExpanded = expanded;
    setExporting("png");
    setExpanded(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(node, {
        backgroundColor: "#0b0b0d",
        scale: 2,
        useCORS: true,
        onclone: (doc) => {
          const clone = doc.querySelector("[data-matchup-export]");
          if (!clone) return;
          clone.style.padding = "18px";
          const title = doc.createElement("div");
          title.textContent = exportTitle;
          title.style.fontWeight = "800";
          title.style.fontSize = "15px";
          title.style.lineHeight = "1.5";
          title.style.color = theme.main;
          title.style.marginBottom = "12px";
          clone.insertBefore(title, clone.firstChild);
          // ปุ่มพวกนี้ไม่ต้องติดไปในรูป
          clone.querySelectorAll("[data-export-hide]").forEach((el) => el.remove());
        },
      });
      await new Promise((resolve) => canvas.toBlob((b) => { if (b) saveBlob(b, `${fileStem}.png`); resolve(); }, "image/png"));
    } catch (e) {
      console.error("matchup png export failed:", e);
    } finally {
      if (!wasExpanded) setExpanded(false);
      setExporting("");
    }
  };

  return (
    <div
      ref={panelRef}
      data-matchup-export="1"
      style={{
        marginTop: 22,
        paddingTop: 18,
        borderTop: "1px solid rgba(255,16,38,0.25)",
        textAlign: "left",
      }}
    >
      <div
        data-export-hide
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div style={{ fontWeight: 900, fontSize: "1.12rem", color: theme.main }}>
          เจอตัวไหนบ่อย
        </div>
        <div style={{ fontSize: "0.82rem", color: theme.secondary }}>
          จาก log เกม {matchup?.totalGames || 0} เกม
          {side === "them" &&
            (!hasScouted
              ? ` (เฉพาะที่เจอ ${TEAM_SHORT})`
              : source === "us"
              ? ` (เฉพาะที่เจอ ${TEAM_SHORT})`
              : source === "scouted"
              ? " (เฉพาะที่ส่องมา)"
              : ` (เจอ ${TEAM_SHORT} + ที่ส่องมา)`)}
          {matchup?.totalGames > 0 && (
            <>
              {" · "}
              <b style={{ color: theme.success }}>{matchup.wins}</b>
              <span>-</span>
              <b style={{ color: theme.danger }}>{matchup.losses}</b>
              {` (${matchup.winRate}%)`}
            </>
          )}
        </div>
      </div>

      {canScope && (
        <div data-export-hide style={{ marginBottom: 9 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.8rem", color: theme.secondary, fontWeight: 700 }}>ช่วงข้อมูล</span>
            {/* ทั้งซีซั่นมาก่อน เพราะเป็นกองตั้งต้นและเป็นกองเดียวที่กว้างพอจะตัดต่อได้ */}
            <MinGamesChip
              $active={scope === "season" && !windowDays}
              onClick={() => { setWindowDays(0); onScopeChange("season"); }}
            >
              {scopeLoading
                ? "กำลังโหลด..."
                : `ทั้งซีซั่น${scopeCounts?.season ? ` (${scopeCounts.season})` : ""}`}
            </MinGamesChip>
            <MinGamesChip
              $active={scope === "current" && !windowDays}
              onClick={() => { setWindowDays(0); onScopeChange("current"); }}
            >
              ช่วงนี้{scopeCounts?.current ? ` (${scopeCounts.current})` : ""}
            </MinGamesChip>
            {WINDOW_CHOICES.map((d) => (
              <MinGamesChip
                key={d}
                $active={scope === "season" && windowDays === d}
                onClick={() => { setWindowDays(d); onScopeChange("season"); }}
                title={`ย้อนหลัง ${d} วันจากเกมล่าสุด`}
              >
                {d} วัน
              </MinGamesChip>
            ))}
          </div>

          {typeChoices.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
              <span style={{ fontSize: "0.8rem", color: theme.secondary, fontWeight: 700 }}>ประเภทลีค</span>
              <MinGamesChip $active={gameType === "all"} onClick={() => setGameType("all")}>
                ทั้งหมด
              </MinGamesChip>
              {typeChoices.map((t) => (
                <MinGamesChip
                  key={t.key}
                  $active={gameType === t.key}
                  onClick={() => pickType(t)}
                  title={
                    t.inPool
                      ? `${t.type} ${t.count} เกมในช่วงที่เลือก`
                      : `${t.type} ไม่มีในช่วงนี้ — กดแล้วจะเปิด log ทั้งซีซั่นให้ (${t.count} เกม)`
                  }
                >
                  {t.type} ({t.count})
                </MinGamesChip>
              ))}
            </div>
          )}
        </div>
      )}

      {hasScouted && typeof onSourceChange === "function" && (
        <div
          data-export-hide
          style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 9 }}
        >
          <span style={{ fontSize: "0.8rem", color: theme.secondary, fontWeight: 700 }}>
            ข้อมูล {opponentLabel || "คู่แข่ง"}
          </span>
          <MinGamesChip
            $active={source === "us"}
            onClick={() => onSourceChange("us")}
            title={`เฉพาะเกมที่ทีมนี้เจอกับ ${TEAM_SHORT}`}
          >
            เกมกับเรา
          </MinGamesChip>
          <MinGamesChip
            $active={source === "scouted"}
            onClick={() => onSourceChange("scouted")}
            title="เฉพาะชีท Specter_Scrim — เกมของทีมนี้กับทีมอื่น"
          >
            ที่ส่องมา ({scouted.length})
          </MinGamesChip>
          <MinGamesChip
            $active={source === "both"}
            onClick={() => onSourceChange("both")}
            title="รวมทั้งเกมที่เจอเราและที่ส่องมา"
          >
            รวมสองชีท
          </MinGamesChip>
        </div>
      )}

      <div data-export-hide style={{ display: "flex", gap: 7, marginBottom: 12 }}>
        <MatchupTab $active={view === "lane"} onClick={() => setView("lane")}>
          คู่เลนเดียวกัน
        </MatchupTab>
        <MatchupTab $active={view === "enemies"} onClick={() => setView("enemies")}>
          ศัตรูทั้ง 5
        </MatchupTab>
        <MatchupTab $active={view === "teams"} onClick={() => setView("teams")}>
          แยกตามทีม
        </MatchupTab>
      </div>

      <div data-export-hide style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: "0.8rem", color: theme.secondary, fontWeight: 700 }}>ตัดที่</span>
        {[1, 2, 3, 5].map((n) => (
          <MinGamesChip
            key={n}
            $active={minGamesRow === n}
            onClick={() => setMinGamesRow(n)}
            title={n === 1 ? "แสดงทุกแถว" : `แสดงเฉพาะที่เจอกัน ${n} เกมขึ้นไป`}
          >
            {n === 1 ? "ไม่ตัด" : `${n}+`}
          </MinGamesChip>
        ))}
        {hiddenByMin > 0 && (
          <span style={{ fontSize: "0.76rem", color: theme.secondary, marginLeft: 2 }}>
            ซ่อน {hiddenByMin} แถว
          </span>
        )}
        {rows.length > 0 && (
          <span data-export-hide style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <MinGamesChip onClick={exportPng} disabled={!!exporting} title="บันทึกเป็นรูปไว้ส่งในกลุ่ม">
              {exporting === "png" ? "..." : "📷 รูป"}
            </MinGamesChip>
            <MinGamesChip onClick={exportCsv} title="บันทึกเป็น CSV">
              CSV
            </MinGamesChip>
          </span>
        )}
      </div>

      {rows.length === 0 && allRows.length > 0 ? (
        <div
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px dashed rgba(255,255,255,0.14)",
            borderRadius: 12,
            padding: "14px 12px",
            color: theme.secondary,
            fontSize: "0.92rem",
          }}
        >
          ไม่มีคู่ไหนเจอกันถึง {minGamesRow} เกมในช่วงนี้ — กด "ไม่ตัด" เพื่อดูทุกแถว
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px dashed rgba(255,255,255,0.14)",
            borderRadius: 12,
            padding: "14px 12px",
            color: theme.secondary,
            fontSize: "0.92rem",
            lineHeight: 1.55,
          }}
        >
          ไม่พบ log เกมของ{isOneHero ? "ตัวนี้" : "คอมนี้"}ในไฟล์/ช่วงเวลาที่เลือก
          {(gameType !== "all" || windowDays > 0) && (
            <>
              <br />
              ตัวกรองที่เปิดอยู่:{" "}
              {[
                windowDays ? `ย้อนหลัง ${windowDays} วัน` : "",
                gameType !== "all"
                  ? `ประเภท ${typeChoices.find((t) => t.key === gameType)?.type || gameType}`
                  : "",
              ]
                .filter(Boolean)
                .join(" · ")}{" "}
              — ลองกดขยายช่วงหรือเลือก "ทั้งหมด" ดู
            </>
          )}
          {side === "them" && (
            <>
              <br />
              ฝั่ง {opponentLabel} มีแต่สถิติรวมจากชีทสรุป — matchup ดูได้เฉพาะเกมที่เจอกับ FS
            </>
          )}
          {/* ทางตันที่เจอบ่อยสุด: ช่วงปัจจุบันไม่มีเกมเจอทีมนี้ ทั้งที่ทั้งซีซั่นมี — ให้กดต่อได้เลย */}
          {canScope && activeScope === "current" && (
            <button
              onClick={() => onScopeChange("season")}
              disabled={scopeLoading}
              style={{
                display: "block",
                marginTop: 12,
                background: `linear-gradient(135deg, ${theme.main}, ${theme.accent})`,
                border: "none",
                borderRadius: 10,
                color: "#fff",
                fontFamily: "inherit",
                fontWeight: 800,
                fontSize: "0.88rem",
                padding: "9px 16px",
                cursor: scopeLoading ? "default" : "pointer",
                opacity: scopeLoading ? 0.6 : 1,
              }}
            >
              {scopeLoading
                ? "กำลังโหลด..."
                : `ลองดู log ทั้งซีซั่น${scopeCounts?.season ? ` (${scopeCounts.season} เกม)` : ""}`}
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {visibleRows.map((row) => (
              <div key={row.name}>
              <MatchupRow
                onClick={view === "teams" ? undefined : () => toggleRow(row)}
                onKeyDown={
                  view === "teams"
                    ? undefined
                    : (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleRow(row);
                        }
                      }
                }
                role={view === "teams" ? undefined : "button"}
                tabIndex={view === "teams" ? undefined : 0}
                aria-expanded={view === "teams" ? undefined : openRow?.name === row.name}
                $clickable={view !== "teams"}
                $open={openRow?.name === row.name}
              >
                <MatchupBar $pct={topGames ? (row.games / topGames) * 100 : 0} />
                {view !== "teams" && (
                  <span style={{ zIndex: 1, display: "flex", flexShrink: 0 }}>
                    {renderHeroImage(
                      (typeof heroImageOf === "function" && heroImageOf(row.name, row.role)) ||
                        getHeroImage(row.name, row.role),
                      row.name,
                      32
                    )}
                  </span>
                )}
                <span
                  style={{
                    zIndex: 1,
                    flex: 1,
                    fontWeight: 800,
                    color: view === "teams" ? theme.main : theme.white,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.name}
                </span>
                <span style={{ zIndex: 1, color: theme.main, fontWeight: 800, fontSize: "0.9rem" }}>
                  {row.games} เกม
                </span>
                <span style={{ zIndex: 1, fontSize: "0.9rem", whiteSpace: "nowrap" }}>
                  <b style={{ color: theme.success }}>{row.wins}</b>
                  <span style={{ color: theme.secondary }}>-</span>
                  <b style={{ color: theme.danger }}>{row.losses}</b>
                </span>
                <span
                  style={{
                    zIndex: 1,
                    minWidth: 52,
                    textAlign: "right",
                    fontWeight: 900,
                    color: row.winRate >= 50 ? theme.success : theme.danger,
                  }}
                >
                  {row.winRate}%
                </span>
              </MatchupRow>

              {openRow?.name === row.name && view !== "teams" && (
                <div
                  style={{
                    margin: "4px 0 8px 26px",
                    padding: "10px 12px",
                    borderLeft: `2px solid ${theme.main}55`,
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "0 10px 10px 0",
                  }}
                >
                  <div style={{ fontSize: "0.82rem", color: theme.secondary, fontWeight: 800, marginBottom: 8 }}>
                    เจอ {row.name}
                    {row.role ? ` (${row.role})` : ""} แล้ว{side === "them" ? `${opponentLabel || "เขา"}` : "เรา"}หยิบอะไรตอบ
                    {counter?.totalGames ? ` — ${counter.totalGames} เกม ${counter.wins}-${counter.losses}` : ""}
                  </div>
                  {!counter?.answers?.length ? (
                    <div style={{ fontSize: "0.85rem", color: theme.secondary }}>ไม่มีข้อมูลในช่วงที่เลือก</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {counter.answers.slice(0, 6).map((a) => (
                        <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {renderHeroImage(
                            (typeof heroImageOf === "function" && heroImageOf(a.name, a.role)) ||
                              getHeroImage(a.name, a.role),
                            a.name,
                            22
                          )}
                          <span style={{ flex: 1, fontWeight: 700, fontSize: "0.88rem", color: theme.white }}>
                            {a.name}
                          </span>
                          <span style={{ color: theme.main, fontSize: "0.84rem", fontWeight: 700 }}>{a.games} เกม</span>
                          <span style={{ fontSize: "0.84rem", whiteSpace: "nowrap" }}>
                            <b style={{ color: theme.success }}>{a.wins}</b>
                            <span style={{ color: theme.secondary }}>-</span>
                            <b style={{ color: theme.danger }}>{a.losses}</b>
                          </span>
                          <span
                            style={{
                              minWidth: 46,
                              textAlign: "right",
                              fontWeight: 900,
                              fontSize: "0.86rem",
                              color: a.winRate >= 50 ? theme.success : theme.danger,
                            }}
                          >
                            {a.winRate}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </div>
            ))}
          </div>

          {rows.length > 8 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{
                marginTop: 10,
                width: "100%",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 10,
                color: theme.main,
                fontFamily: "inherit",
                fontWeight: 800,
                fontSize: "0.88rem",
                padding: "8px 0",
                cursor: "pointer",
              }}
            >
              {expanded ? "ย่อรายการ" : `ดูทั้งหมด (${rows.length})`}
            </button>
          )}

          {Number(combo?.totalGames) !== matchup.totalGames && (
            <div style={{ marginTop: 10, fontSize: "0.78rem", color: theme.secondary, lineHeight: 1.5 }}>
              {side === "them"
                ? `หมายเหตุ: การ์ด (${combo?.totalGames} เกม) มาจากชีทสรุปของ ${opponentLabel || "ทีมนี้"} ซึ่งนับทุกเกมของเขา ส่วนตารางนี้นับเฉพาะเกมที่เจอกับ FS (${matchup.totalGames} เกม)`
                : activeScope === "season"
                ? `หมายเหตุ: การ์ดนับเฉพาะช่วงปัจจุบัน (${combo?.totalGames} เกม) ส่วนตารางนี้นับ log ทั้งซีซั่น (${matchup.totalGames} เกม)`
                : `หมายเหตุ: ตัวเลขบนการ์ด (${combo?.totalGames} เกม) มาจากชีทสรุป ส่วน matchup นับจาก log เกมที่มีฮีโร่ฝ่ายตรงข้าม (${matchup.totalGames} เกม)`}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ===================== Main Component ===================== */

export default function TierData({
  games,
  comboStats,                 // ← ค่าจาก DraftPage
  onOpponentChange,           // ← callback ไป DraftPage (ไม่บังคับส่ง)
  hideOpponentSelect = false, // ← ถ้า true จะไม่แสดง dropdown ภายใน TierData
  opponentCode,
  dataSource = 'auto',
  dropdownDates = [],
  excludedHeroes = [],

  // --- Series/BO inputs ---
  seriesTakenUs = [],
  seriesTakenThem = [],
  boRounds = 0,                // จำนวนเกมที่เล่นไปในซีรีส์ (0 = ปิดโหมด BO)
  boUsedUs = [],               // ฮีโร่ของเรา (ที่หยิบไปแล้วในเกมก่อน)
  boUsedThem = [],             // ฮีโร่ของเขา (ที่หยิบไปแล้วในเกมก่อน)
  boBans = [],                 // แบนในซีรีส์
  enforceNoRepeatSeries = true,

  rivalFilter = null,
  sideFilter = null,
  minGames = 1,
  contextTitle,
  excelFile = null,

  // รับเป็น alias ชัดเจน และตั้งค่าเริ่มต้นที่นี่
  perspective: perspectiveProp = 'us',
  myPicks: myPicksProp,
  oppPicks: oppPicksProp,
  banList: banListProp,
  simPlayedUs = 0,
  simPlayedThem = 0,
  onApply,
  heroImageOf,

  // ✅ ใหม่ — บริบท BO จาก DraftPage (ใช้กำหนดกฎซ้ำทั้งซีรีส์)
  boType = null,          // 'BO1' | 'BO3' | 'BO7' | null
  currentGame = 1,        // เลขเกมปัจจุบัน
  totalGames = 0          // จำนวนเกมทั้งหมดของซีรีส์ (เช่น 1/3/7)
}) {
  /* ------------ States ------------ */
  const [comboType, setComboType] = useState("all");
  const [rawSearch, setRawSearch] = useState("");
  const searchTerm = useDeferredValue(rawSearch);
  const [sortBy, setSortBy] = useState("mostWins");
  const [chartMode, setChartMode] = useState("winRate");
  const [positionFilter, setPositionFilter] = useState([]);
  const [dateType, setDateType] = useState("day");
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState({ from: "", to: "" });
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedWeekRange, setSelectedWeekRange] = useState({ from: "", to: "" });
  const [teamFilter, setTeamFilter] = useState([]);
  const [teamsFromRplStats, setTeamsFromRplStats] = useState([]);
  const [fsOneHeroFromRplStats, setFsOneHeroFromRplStats] = useState([]);
  const [savedCombos, setSavedCombos] = useState([]);
  const [selectedCombo, setSelectedCombo] = useState(null);
  // ตั้งต้นที่ log ทั้งซีซั่น: กองของช่วงนี้แคบเกินจะตอบ "เจอตัวไหนบ่อย" และมักมี
  // Type เดียว ทำให้เลือกลีกไม่ได้ — ชีทใหญ่ยังโหลดตอนเปิดการ์ดครั้งแรกเหมือนเดิม
  const [matchupScope, setMatchupScope] = useState("season");
  // ฝั่งคู่แข่งอ่านจากชีทไหน: เกมที่เจอเรา / ที่ส่องมา / รวมสองชีท
  const [matchupSource, setMatchupSource] = useState("both");
  const [showToast, setShowToast] = useState({ visible: false, message: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState("");
  const [coachQuestion, setCoachQuestion] = useState("");
  const [coachResult, setCoachResult] = useState(null);
  const itemsPerPage = 12;

  /* -------- Perspective (ไม่ล็อกด้วย prop) -------- */
  const [perspective, setPerspective] = useState(perspectiveProp ?? "us");
  useEffect(() => {
    if (perspectiveProp !== undefined) setPerspective(perspectiveProp);
  }, [perspectiveProp]);

  const [opponent, setOpponent] = useState(opponentCode || "BRU");


  useEffect(() => {
    if (opponentCode && opponentCode !== opponent) {
      setOpponent(opponentCode);
    }
  }, [opponentCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const [comboStatsOppLocal, setComboStatsOppLocal] = useState({
    combo2: [],
    combo3: [],
    oneHero: [],
  });
  const [loadingOpp, setLoadingOpp] = useState(false);

  // โหลดรายการทีมจากตารางฟิลเตอร์ในชีท RPL_Stats (ถ้ามี)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFile) {
        setTeamsFromRplStats([]);
        return;
      }
      try {
        const teams = await parseRplStatsTeamsP(excelFile);
        if (!cancelled) setTeamsFromRplStats(Array.isArray(teams) ? teams : []);
      } catch (e) {
        if (!cancelled) setTeamsFromRplStats([]);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFile]);

  // โหลด one-hero ของฝั่ง FS จาก RPL_player_stats
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFile) {
        setFsOneHeroFromRplStats([]);
        return;
      }
      try {
        const list = await parseFsOneHeroFromRplPlayerStatsP(excelFile);
        if (!cancelled) setFsOneHeroFromRplStats(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setFsOneHeroFromRplStats([]);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFile]);

  // โหลดข้อมูล "เขา" : พยายาม Competitor_Hero_stats ก่อน ถ้าไม่มีให้ fallback ไปชีททีม
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!excelFile) { setComboStatsOppLocal({ combo2: [], combo3: [] }); return; }
      setLoadingOpp(true);
      try {
        const oppComp = await parseOpponentCombosForTeam(excelFile, opponent);
        const norm = normalizeOppToComboStats(oppComp);
        const hasComp =
          (norm.combo2?.length || 0) +
          (norm.combo3?.length || 0) +
          (norm.oneHero?.length || 0) > 0;

        if (!cancelled && hasComp) {
          // ✅ เก็บ oneHero มาด้วย
          setComboStatsOppLocal(norm);
        }

        if (!hasComp) {
          const res = await parseWorkbookToComboStats(excelFile, {
            sheets: [opponent],
            includeOneHero: true,
          });
          if (!cancelled) {
            setComboStatsOppLocal({
              combo2: res.combo2 || [],
              combo3: res.combo3 || [],
              oneHero: res.oneHero || [],
            });
          }
        }

      } catch (e) {
        if (!cancelled) setComboStatsOppLocal({ combo2: [], combo3: [] });
      } finally {
        if (!cancelled) setLoadingOpp(false);
      }
    })();
    return () => { cancelled = true; };
  }, [excelFile, opponent]);

  /* Draft picks/bans (รับจาก prop ได้ + safety to array) */
  const [myPicksState, setMyPicks] = useState([]);
  const [oppPicksState, setOppPicks] = useState([]);
  const [banListState, setBanList] = useState([]);

  const myPicks = asArray(myPicksProp ?? myPicksState);
  const oppPicks = asArray(oppPicksProp ?? oppPicksState);
  const banList = asArray(banListProp ?? banListState);

  /* -------- Period/Team filters -------- */
  const dateOptions = useMemo(() => {
    return dropdownDates.length > 0
      ? dropdownDates
      : [...new Set(games.map((g) => g.Date).filter(Boolean))].sort();
  }, [dropdownDates, games]);

  const weekMap = useMemo(() => buildWeekMap(dateOptions), [dateOptions]);
  const weekOptions = useMemo(
    () => [...new Set(Object.values(weekMap || {}))].sort((a, b) => +a.slice(1) - +b.slice(1)),
    [weekMap]
  );

  const filterByPeriodAndTeam = useCallback((arr) => {
    let res = arr;
    if (dateType === "day") {
      if (selectedDates.length) {
        res = res.filter((g) => selectedDates.includes(g.Date));
      } else if (selectedDateRange.from && selectedDateRange.to) {
        res = res.filter((g) => g.Date >= selectedDateRange.from && g.Date <= selectedDateRange.to);
      }
    } else {
      if (selectedWeeks.length) {
        res = res.filter((g) => selectedWeeks.includes(weekMap[g.Date]));
      } else if (selectedWeekRange.from && selectedWeekRange.to) {
        const slice = weekOptions.slice(
          weekOptions.indexOf(selectedWeekRange.from),
          weekOptions.indexOf(selectedWeekRange.to) + 1
        );
        res = res.filter((g) => slice.includes(weekMap[g.Date]));
      }
    }
    if (teamFilter.length) {
      res = res.filter((g) => teamFilter.includes(g.competitor));
    }
    return res;
  }, [
    dateType,
    selectedDates,
    selectedDateRange,
    selectedWeeks,
    selectedWeekRange,
    teamFilter,
    weekMap,
    weekOptions
  ]);

  const periodTeamFiltered = useMemo(
    () => filterByPeriodAndTeam(games),
    [games, filterByPeriodAndTeam]
  );

  /* -------- Exclusions -------- */
  const exSet = useMemo(
    () => new Set((excludedHeroes || []).map((h) => safeHeroKey(h))),
    [excludedHeroes]
  );

  // ✅ เพิ่ม series-block แยกตามฝั่ง (เคารพกติกา BO: เกมสุดท้ายของ BO7 ไม่บังคับห้ามซ้ำ)
  const allowRepeatNow = useMemo(() => {
    const isValid = boType && totalGames > 0;
    if (!isValid) return false;
    const isLastGame = Number(currentGame) === Number(totalGames);
    return String(boType).toUpperCase() === "BO7" && isLastGame;
  }, [boType, currentGame, totalGames]);

  const seriesBlockUsSet = useMemo(
    () => new Set(((enforceNoRepeatSeries && !allowRepeatNow) ? seriesTakenUs : []).map(safeHeroKey)),
    [seriesTakenUs, enforceNoRepeatSeries, allowRepeatNow]
  );
  const seriesBlockThemSet = useMemo(
    () => new Set(((enforceNoRepeatSeries && !allowRepeatNow) ? seriesTakenThem : []).map(safeHeroKey)),
    [seriesTakenThem, enforceNoRepeatSeries, allowRepeatNow]
  );

  const filteredGames = useMemo(() => {
    return (periodTeamFiltered || []).filter((g) => {
      const picks = (g.fsPick || []).map((x) => safeHeroKey(x));
      if (picks.some((h) => exSet.has(h))) return false;
      if (rivalFilter) {
        const rv = String(g.rival || "").toUpperCase().trim();
        if (rv !== String(rivalFilter).toUpperCase().trim()) return false;
      }
      if (sideFilter) {
        const sd = (g.side || "").toString().trim().toLowerCase();
        if (sd !== String(sideFilter).toLowerCase()) return false;
      }
      return true;
    });
  }, [periodTeamFiltered, exSet, rivalFilter, sideFilter]);

  // สำหรับ fallback stat (กรณีไม่มี summary จากชีทสถิติ): ให้ตรงกับมุมมองในไฟล์ที่ใช้ RPL/Scrim
  const fallbackStatGames = useMemo(() => {
    return (filteredGames || []).filter((g) => {
      const t = String(g?.type || "").trim().toLowerCase();
      return t === "rpl" || t === "scrim";
    });
  }, [filteredGames]);

  // เมื่อมีการฟิลเตอร์ทีม/ช่วงเวลา ให้คำนวณสถิติฝั่งเราแบบ per-scope จากเกมที่ถูกกรอง
  // เพื่อหลีกเลี่ยงการใช้ค่า aggregate รวมทุกทีมจาก summary sheet
  const useScopedUsFromGames = useMemo(() => {
    if (perspective !== "us") return false;
    if (teamFilter.length > 0) return true;
    if (selectedDates.length > 0) return true;
    if (selectedDateRange.from && selectedDateRange.to) return true;
    if (selectedWeeks.length > 0) return true;
    if (selectedWeekRange.from && selectedWeekRange.to) return true;
    if (rivalFilter) return true;
    if (sideFilter) return true;
    return false;
  }, [
    perspective,
    teamFilter,
    selectedDates,
    selectedDateRange,
    selectedWeeks,
    selectedWeekRange,
    rivalFilter,
    sideFilter
  ]);

  // fallback ฝั่งคู่แข่งจาก Girl_Scrim/All_Scrim_Stats โดยตรง (กรณียังไม่มีชีททีมแยก)
  const opponentFallbackComboStats = useMemo(() => {
    const oppCode = normalizeTeamCode(opponent);
    if (!oppCode) return { combo2: [], combo3: [], oneHero: [] };

    const oppGames = (fallbackStatGames || []).filter(
      (g) => normalizeTeamCode(g?.competitor) === oppCode
    );
    if (!oppGames.length) return { combo2: [], combo3: [], oneHero: [] };

    const combo2 = [
      ...buildPairComboListBySide(oppGames, "MID", "SUP", "2-heroes (MID+SUP)", "op"),
      ...buildPairComboListBySide(oppGames, "ADL", "SUP", "2-heroes (ADL+SUP)", "op"),
      ...buildPairComboListBySide(oppGames, "DSL", "JUG", "2-heroes (DSL+Jungle)", "op"),
      ...buildPairComboListBySide(oppGames, "JUG", "SUP", "2-heroes (JUG+SUP)", "op"),
      ...buildPairComboListBySide(oppGames, "JUG", "MID", "2-heroes (JUG+MID)", "op"),
    ];
    const combo3 = [
      ...buildTripleComboListBySide(oppGames, "JUG", "MID", "SUP", "3-heroes (JUG+MID+SUP)", "op"),
      ...buildTripleComboListBySide(oppGames, "JUG", "ADL", "SUP", "3-heroes (JUG+ADL+SUP)", "op"),
      ...buildTripleComboListBySide(oppGames, "MID", "ADL", "SUP", "3-heroes (MID+ADL+SUP)", "op"),
    ];
    const oneHero = buildOneHeroListBySide(oppGames, "op");

    return { combo2, combo3, oneHero };
  }, [fallbackStatGames, opponent]);

  /* -------- Helper: topPlayed -------- */
  function topPlayed(_side, N) {
    if (!N) return new Set();
    const counter = Object.create(null);
    for (const g of fallbackStatGames) {
      const picks = (g.fsPick || []).map(canonicalHeroName).filter(Boolean);
      for (const h of picks) counter[h] = (counter[h] || 0) + 1;
    }
    return new Set(
      Object.entries(counter)
        .sort((a, b) => b[1] - a[1])
        .slice(0, Math.max(0, N))
        .map(([h]) => h)
    );
  }

  /* -------- block sets (หลัง filteredGames เพื่อกัน TDZ) -------- */
  const blockForUs = useMemo(() => {
    const sim = topPlayed('us', simPlayedUs) || new Set();
    const pool = [
      ...myPicks, ...oppPicks, ...banList, ...Array.from(sim),
      ...(boRounds > 0 ? asArray(boUsedUs) : []),
      ...(boRounds > 0 ? asArray(boUsedThem) : []),
      ...(boRounds > 0 ? asArray(boBans) : []),
    ];
    return new Set(pool.map(safeHeroKey));
  }, [myPicks, oppPicks, banList, simPlayedUs, boRounds, boUsedUs, boUsedThem, boBans]);

  const blockForThem = useMemo(() => {
    const sim = topPlayed('them', simPlayedThem) || new Set();
    const pool = [
      ...oppPicks, ...myPicks, ...banList, ...Array.from(sim),
      ...(boRounds > 0 ? asArray(boUsedUs) : []),
      ...(boRounds > 0 ? asArray(boUsedThem) : []),
      ...(boRounds > 0 ? asArray(boBans) : []),
    ];
    return new Set(pool.map(safeHeroKey));
  }, [myPicks, oppPicks, banList, simPlayedThem, boRounds, boUsedUs, boUsedThem, boBans]);

  const finalBlockSetUs = useMemo(() => {
    if (!(enforceNoRepeatSeries && !allowRepeatNow)) return blockForUs;
    return new Set([...blockForUs, ...seriesBlockUsSet]);
  }, [blockForUs, seriesBlockUsSet, enforceNoRepeatSeries, allowRepeatNow]);

  const finalBlockSetThem = useMemo(() => {
    if (!(enforceNoRepeatSeries && !allowRepeatNow)) return blockForThem;
    return new Set([...blockForThem, ...seriesBlockThemSet]);
  }, [blockForThem, seriesBlockThemSet, enforceNoRepeatSeries, allowRepeatNow]);

  const hasScopeFilter = useMemo(() => {
    return (
      teamFilter.length > 0 ||
      selectedDates.length > 0 ||
      selectedWeeks.length > 0 ||
      Boolean(selectedDateRange.from || selectedDateRange.to) ||
      Boolean(selectedWeekRange.from || selectedWeekRange.to)
    );
  }, [teamFilter, selectedDates, selectedWeeks, selectedDateRange, selectedWeekRange]);

  /* -------- 1-hero list (จาก games) -------- */
  const oneHeroMetricMap = useMemo(() => {
    const m = new Map();
    const gameCount = fallbackStatGames.length;

    for (const g of fallbackStatGames) {
      const picks = g.fsPick || [];
      const positions = (g.fsPosition || []).map(normalizePosition);
      const raw = Array.isArray(g.raw) ? g.raw : [];
      const teamKills = numOrNull(raw[74]);
      const isWin = g.win === 1 || g.win === "1" || String(g.result || "").toLowerCase() === "win";

      for (let i = 0; i < picks.length; i++) {
        const hero = canonicalHeroName(picks[i]);
        const position = positions[i];
        const cols = FS_ROLE_METRIC_COLS[position];
        if (!hero || !position || !cols) continue;

        const key = `${hero}|||${position}`;
        if (!m.has(key)) {
          m.set(key, {
            hero,
            position,
            total: 0,
            wins: 0,
            kdaSum: 0,
            kdaCount: 0,
            partSum: 0,
            partCount: 0,
            gpmSum: 0,
            gpmCount: 0,
            dmgMinSum: 0,
            dmgMinCount: 0,
            poolGames: gameCount,
          });
        }
        const slot = m.get(key);
        slot.total += 1;
        if (isWin) slot.wins += 1;

        const k = numOrNull(raw[cols.kill]);
        const d = numOrNull(raw[cols.death]);
        const a = numOrNull(raw[cols.assist]);
        if (k !== null && d !== null && a !== null) {
          slot.kdaSum += (k + a) / Math.max(1, d);
          slot.kdaCount += 1;
        }
        if (teamKills !== null && teamKills > 0 && k !== null && a !== null) {
          slot.partSum += ((k + a) / teamKills) * 100;
          slot.partCount += 1;
        }

        const gpm = numOrNull(raw[cols.gpm]);
        if (gpm !== null) {
          slot.gpmSum += gpm;
          slot.gpmCount += 1;
        }
        const dmgMin = numOrNull(raw[cols.dmgMin]);
        if (dmgMin !== null) {
          slot.dmgMinSum += dmgMin;
          slot.dmgMinCount += 1;
        }
      }
    }

    return m;
  }, [fallbackStatGames]);

  const heroListFromGames = useMemo(() => {
    return [...oneHeroMetricMap.values()].map((slot) => {
      const total = slot.total;
      const wins = slot.wins;
      const losses = total - wins;
      const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
      const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;
      const avgKDA = slot.kdaCount ? Number((slot.kdaSum / slot.kdaCount).toFixed(2)) : null;
      const avgParticipation = slot.partCount ? Number((slot.partSum / slot.partCount).toFixed(1)) : null;
      const avgGPM = slot.gpmCount ? Number((slot.gpmSum / slot.gpmCount).toFixed(1)) : null;
      const avgDmgPerMin = slot.dmgMinCount ? Number((slot.dmgMinSum / slot.dmgMinCount).toFixed(1)) : null;
      const pickRate = slot.poolGames > 0 ? Number(((total / slot.poolGames) * 100).toFixed(1)) : null;
      return {
        name: slot.hero,
        position: slot.position,
        comboType: "1-hero",
        characters: [slot.hero],
        images: [getHeroImage(slot.hero, slot.position)],
        wins,
        totalGames: total,
        losses,
        winRate,
        loseRate,
        avgKDA,
        avgParticipation,
        avgGPM,
        avgDmgPerMin,
        pickRate,
      };
    });
  }, [oneHeroMetricMap]);

  // ฝั่งเรา: ใช้ RPL_player_stats ก่อน แล้วค่อย fallback เป็นคำนวณจาก games
  const heroListUs = useMemo(() => {
    const metricByKey = new Map(heroListFromGames.map((h) => [`${h.name}|||${h.position}`, h]));
    if ((fsOneHeroFromRplStats || []).length === 0 || hasScopeFilter) return heroListFromGames;
    return fsOneHeroFromRplStats.map((h) => {
      const name = canonicalHeroName(h.hero || h.name);
      const position = (h.position || "").toUpperCase();
      const total = h.total ?? h.totalGames ?? 0;
      const wins = h.win ?? h.wins ?? 0;
      const losses = Math.max(0, total - wins);
      const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
      const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;
      const metrics = metricByKey.get(`${name}|||${position}`);
      return {
        name,
        position,
        comboType: "1-hero",
        characters: [name],
        images: [getHeroImage(name, position)],
        wins,
        totalGames: total,
        losses,
        winRate,
        loseRate,
        avgKDA: metrics?.avgKDA ?? null,
        avgParticipation: metrics?.avgParticipation ?? null,
        avgGPM: metrics?.avgGPM ?? null,
        avgDmgPerMin: metrics?.avgDmgPerMin ?? null,
        pickRate: metrics?.pickRate ?? null,
      };
    });
  }, [fsOneHeroFromRplStats, heroListFromGames, hasScopeFilter]);

  /* -------- combo2 / combo3 จาก effectiveComboStats -------- */
  const EMPTY_COMBOS = { combo2: [], combo3: [], oneHero: [] };

  const effectiveComboStats = useMemo(() => {
    if (perspective === "us" && useScopedUsFromGames) {
      return EMPTY_COMBOS;
    }

    const hasProp =
      (comboStats?.combo2?.length || 0) +
      (comboStats?.combo3?.length || 0) +
      (comboStats?.oneHero?.length || 0) > 0;

    if (dataSource === 'props') {
      return hasProp ? comboStats : EMPTY_COMBOS;
    }

    if (perspective === 'them') {
      const hasLocal =
        (comboStatsOppLocal?.combo2?.length || 0) +
        (comboStatsOppLocal?.combo3?.length || 0) +
        (comboStatsOppLocal?.oneHero?.length || 0) > 0;
      const hasFallbackFromGames =
        (opponentFallbackComboStats?.combo2?.length || 0) +
        (opponentFallbackComboStats?.combo3?.length || 0) +
        (opponentFallbackComboStats?.oneHero?.length || 0) > 0;

      if (hasLocal) return comboStatsOppLocal;
      if (hasFallbackFromGames) return opponentFallbackComboStats;
      if (hasProp) return comboStats;
      return EMPTY_COMBOS;
    }

    return hasProp ? comboStats : EMPTY_COMBOS;
  }, [dataSource, perspective, useScopedUsFromGames, comboStatsOppLocal, opponentFallbackComboStats, comboStats]);


  const combo2List = useMemo(() => {
    return (effectiveComboStats?.combo2 || []).map((c) => {
      const chars = c.combo.split(" + ").map((s) => canonicalHeroName(s));
      const comboName = chars.join(" + ");
      const total = c.total ?? c.totalGames ?? 0;
      const wins = c.win ?? c.wins ?? 0;
      const losses = total - wins;
      const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
      const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;
      return {
        name: comboName,
        comboType: normalizeComboTypeLabel(c.comboType || c.combo),
        characters: chars,
        images: chars.map(getHeroImage),
        wins,
        totalGames: total,
        losses,
        winRate,
        loseRate
      };
    });
  }, [effectiveComboStats?.combo2]);

  const combo3List = useMemo(() => {
    return (effectiveComboStats?.combo3 || []).map((c) => {
      const chars = c.combo.split(" + ").map((s) => canonicalHeroName(s));
      const comboName = chars.join(" + ");
      const total = c.total || 0;
      const wins = c.win || 0;
      const losses = total - wins;
      const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
      const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;
      return {
        name: comboName,
        comboType: normalizeComboTypeLabel(c.comboType || "3-heroes"),
        characters: chars,
        images: chars.map(getHeroImage),
        wins,
        totalGames: total,
        losses,
        winRate,
        loseRate
      };
    });
  }, [effectiveComboStats?.combo3]);
  // 1-hero ของฝั่ง "เขา" ที่ได้จาก excelParser (normalizeOppToComboStats)
  const heroListOpp = useMemo(() => {
    if (perspective !== "them") return [];
    const list = effectiveComboStats?.oneHero || [];
    return list.map((h) => {
      const name = canonicalHeroName(h.hero || h.name);
      const position = (h.position || "").toUpperCase();
      const total = h.total ?? h.totalGames ?? 0;
      const wins = h.win ?? h.wins ?? 0;
      const losses = Math.max(0, total - wins);
      const winRate = total ? Number(((wins / total) * 100).toFixed(1)) : 0;
      const loseRate = total ? Number(((1 - wins / total) * 100).toFixed(1)) : 0;

      return {
        name,
        position,
        comboType: "1-hero",
        characters: [name],
        images: [getHeroImage(name, position)],
        wins,
        totalGames: total,
        losses,
        winRate,
        loseRate,
        avgKda: h.avgKda ?? null,
        avgParticipation: h.avgParticipation ?? null,
        avgGpm: h.avgGpm ?? null,
        avgDmgPerMin: h.avgDmgPerMin ?? null,
        pickRate: h.pickRate ?? null
      };
    });
  }, [effectiveComboStats, perspective]);

  /* -------- Derived combos จาก games (เฉพาะโหมดเรา) -------- */
  const combo2MidSupList = useMemo(() => buildPairComboList(fallbackStatGames, "MID", "SUP", "2-heroes (MID+SUP)"), [fallbackStatGames]);
  const combo2ADLSupList = useMemo(() => buildPairComboList(fallbackStatGames, "ADL", "SUP", "2-heroes (ADL+SUP)"), [fallbackStatGames]);
  const combo2DSLJugList = useMemo(() => buildPairComboList(fallbackStatGames, "DSL", "JUG", "2-heroes (DSL+Jungle)"), [fallbackStatGames]);
  const combo2JUGSupList = useMemo(() => buildPairComboList(fallbackStatGames, "JUG", "SUP", "2-heroes (JUG+SUP)"), [fallbackStatGames]);
  const combo2JUGMidList = useMemo(() => buildPairComboList(fallbackStatGames, "JUG", "MID", "2-heroes (JUG+MID)"), [fallbackStatGames]);

  /* NEW: 3-heroes (สร้างจาก games) */
  const combo3JUGMidSupList = useMemo(
    () => buildTripleComboList(fallbackStatGames, "JUG", "MID", "SUP", "3-heroes (JUG+MID+SUP)"),
    [fallbackStatGames]
  );
  const combo3JUGAdlSupList = useMemo(
    () => buildTripleComboList(fallbackStatGames, "JUG", "ADL", "SUP", "3-heroes (JUG+ADL+SUP)"),
    [fallbackStatGames]
  );
  const combo3MidAdlSupList = useMemo(
    () => buildTripleComboList(fallbackStatGames, "MID", "ADL", "SUP", "3-heroes (MID+ADL+SUP)"),
    [fallbackStatGames]
  );

  const combo5List = useMemo(
    () => buildFiveCombosFromGames(fallbackStatGames),
    [fallbackStatGames]
  );

  /* -------- Matchup ของการ์ดที่กด: เจอตัวไหนบ่อย + วินเรท --------
     ต้องอ่านจาก log เกมต่อเกมเท่านั้น (ชีทสรุปไม่มีฮีโร่ฝ่ายตรงข้ามรายเกม)
     ฝั่งเขา: ใช้ได้แค่เกมที่เจอกับ FS เพราะไฟล์เรามี log เท่านั้น
     scope "season": โหลด log ทั้งซีซั่นจากชีท All_Scrim ตอนกดครั้งแรก (ไฟล์ใหญ่) */
  const { log: seasonLog, loading: seasonLogLoading } = useSeasonLog(
    excelFile,
    matchupScope === "season" && !!selectedCombo
  );
  // ชีทส่องเล็กกว่า All_Scrim มาก แต่ใช้เฉพาะฝั่งคู่แข่ง — โหลดตอนเปิดการ์ดเหมือนกัน
  const { log: specterLog } = useSeasonLog(
    excelFile,
    !!selectedCombo && perspective === "them",
    parseSpecterScrimP
  );
  const scoutedGames = useMemo(
    () =>
      perspective === "them" && opponent
        ? specterGamesForTeam(specterLog?.games, opponent)
        : [],
    [specterLog, perspective, opponent]
  );

  // ไม่กรองประเภทตรงนี้แล้ว — แผงมี chip ให้เลือกเอง (Scrim / RPL / s.Taiwan / Coda)
  const seasonStatGames = useMemo(() => {
    const raw = seasonLog?.games || [];
    if (!raw.length) return [];
    return filterByPeriodAndTeam(raw);
  }, [seasonLog, filterByPeriodAndTeam]);

  const matchupPool = useMemo(
    () => (matchupScope === "season" && seasonStatGames.length ? seasonStatGames : filteredGames),
    [matchupScope, seasonStatGames, filteredGames]
  );

  const narrowToOpponent = useCallback((arr) => {
    if (perspective !== "them") return arr;
    const code = String(opponent || "").trim().toUpperCase();
    if (!code) return [];
    return arr.filter(
      (g) => String(g?.competitor || "").trim().toUpperCase() === code
    );
  }, [perspective, opponent]);

  const matchupGames = useMemo(
    () => narrowToOpponent(matchupPool),
    [narrowToOpponent, matchupPool]
  );
  // กองทั้งซีซั่นส่งเข้าแผงด้วย เพื่อให้ chip ลีกยังเลือกได้ตอนอยู่ที่ "ช่วงนี้"
  const seasonMatchupGames = useMemo(
    () => narrowToOpponent(seasonStatGames),
    [narrowToOpponent, seasonStatGames]
  );


  /* -------- Select options -------- */
  const teamOptions = useMemo(
    () => (
      teamsFromRplStats.length
        ? teamsFromRplStats
        : [...new Set(games.map((g) => g.competitor).filter(Boolean))].sort()
    ),
    [games, teamsFromRplStats]
  );
  const opponentOptions = useMemo(() => {
    const set = new Set();
    teamOptions.forEach((t) => {
      const code = String(t || "").trim().toUpperCase();
      if (code && code !== "FS") set.add(code);
    });
    return [...set].sort();
  }, [teamOptions]);

  useEffect(() => {
    if (!opponentOptions.length) return;
    if (!opponent || !opponentOptions.includes(opponent)) {
      setOpponent(opponentOptions[0]);
    }
  }, [opponent, opponentOptions]);
  const positionOptions = useMemo(
    () => [...new Set(heroListUs.map((h) => h.position))].sort(),
    [heroListUs]
  );

  const dateOptionsForSelect = useMemo(
    () => dateOptions.map((d) => ({ value: d, label: d })),
    [dateOptions]
  );
  const weekOptionsForSelect = useMemo(
    () => weekOptions.map((w) => ({ value: w, label: w })),
    [weekOptions]
  );
  const teamOptionsForSelect = useMemo(
    () => teamOptions.map((t) => ({ value: t, label: t })),
    [teamOptions]
  );

  useEffect(() => {
    setTeamFilter((prev) => prev.filter((t) => teamOptions.includes(t)));
  }, [teamOptions]);

  const allHeroNames = useMemo(() => {
    const from1 = heroListUs.map(h => h.name);
    const from2 = (effectiveComboStats?.combo2 || [])
      .flatMap(c => c.combo.split(" + ").map(s => canonicalHeroName(s)));
    const from3 = (effectiveComboStats?.combo3 || [])
      .flatMap(c => c.combo.split(" + ").map(s => canonicalHeroName(s)));
    return [...new Set([...from1, ...from2, ...from3])].sort();
  }, [heroListUs, effectiveComboStats]);


  const heroOptionsForSelect = useMemo(
    () => allHeroNames.map(h => ({ value: h, label: h })),
    [allHeroNames]
  );

  const isDateRangeIncomplete =
    dateType === "day" && selectedDateRange.from && !selectedDateRange.to;
  const isWeekRangeIncomplete =
    dateType === "week" && selectedWeekRange.from && !selectedWeekRange.to;

  /* -------- Merge combos (All tab) -------- */
  const combos = useMemo(() => {
    // ฝั่งเรา: นับคอมโบจาก log เกมเป็นหลัก แล้วใช้ชีทสรุปเป็น fallback เมื่อไฟล์ไม่มี log
    // (log ให้ตัวเลขชุดเดียวกับแผง matchup และการ์ด 5 ตัว ไม่หลุดกันเอง — ชีทบางไฟล์
    //  มีแต่บล็อก 2 ตัว ทำให้แท็บ 3 ตัวว่าง และคอลัมน์ win ในบางเลย์เอาต์ก็อ่านมาผิด)
    const gameDerived2 = [
      ...combo2MidSupList,
      ...combo2ADLSupList,
      ...combo2DSLJugList,
      ...combo2JUGSupList,
      ...combo2JUGMidList,
    ];
    const gameDerived3 = [
      ...combo3JUGMidSupList,
      ...combo3JUGAdlSupList,
      ...combo3MidAdlSupList,
    ];
    const preferGames = (gameList, sheetList) => (gameList.length ? gameList : sheetList);
    const usComboStats = [
      ...preferGames(gameDerived2, combo2List),
      ...preferGames(gameDerived3, combo3List),
    ];

    const usBase = [
      ...heroListUs,
      ...combo5List
    ];

    const themBase = [
      ...heroListOpp,   // ✅ 1-hero ของฝั่งเขา
      ...combo2List,
      ...combo3List,
    ];

    const list = (perspective === "us")
      ? [...usBase, ...usComboStats]
      : themBase;

    const filtered = list.filter((c) => !c.name.toLowerCase().startsWith("grand total"));
    const m = new Map();
    for (const it of filtered) {
      const key = `${it.comboType}|${it.name}|${it.position || ""}`;
      const cur = m.get(key);
      if (!cur || (it.totalGames || 0) > (cur.totalGames || 0)) m.set(key, it);
    }
    return [...m.values()];
  }, [
    perspective,
    heroListUs,
    heroListOpp,            // ✅ เพิ่มใน deps
    combo2List,
    combo2MidSupList,
    combo2ADLSupList,
    combo2DSLJugList,
    combo2JUGSupList,
    combo2JUGMidList,
    combo3List,
    combo3JUGMidSupList,
    combo3JUGAdlSupList,
    combo3MidAdlSupList,
    combo5List
  ]);


  /* -------- Apply filters -------- */
  let showCombos = combos;

  showCombos = showCombos.filter((c) => comboTypeMatches(comboType, c.comboType));

  if (comboType === "1-hero" && positionFilter.length > 0) {
    showCombos = showCombos.filter((c) => positionFilter.includes(c.position));
  }

  showCombos = showCombos.filter((c) => (c.totalGames || 0) >= (minGames || 1));

  function keepAvailable(combo, blockSet) {
    const chars = asArray(combo?.characters).map(safeHeroKey);
    const set = blockSet instanceof Set ? blockSet : new Set();
    if (chars.length === 0) return true;
    return !chars.some(ch => set.has(ch));
  }

  const activeBlockSet = perspective === "us" ? finalBlockSetUs : finalBlockSetThem;
  showCombos = showCombos.filter(c => keepAvailable(c, activeBlockSet));

  // Search & Sort
  showCombos = showCombos.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  showCombos.sort((a, b) => {
    if (sortBy === "winRate") return b.winRate - a.winRate || b.totalGames - a.totalGames;
    if (sortBy === "loseRate") return b.loseRate - a.loseRate || b.totalGames - a.totalGames;
    if (sortBy === "totalGames") return b.totalGames - a.totalGames || b.winRate - a.winRate;
    if (sortBy === "mostLosses") return b.losses - a.losses || b.totalGames - a.totalGames;
    return b.wins - a.wins || b.totalGames - a.totalGames;
  });

  const currentCombos = showCombos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const focusLabel = buildTierFocusLabel(comboType);
  const strictComboType = comboType !== "all";
  const focusTop = [...showCombos]
    .sort((a, b) => b.winRate - a.winRate || b.totalGames - a.totalGames)
    .slice(0, 10)
    .map((c) => ({
      name: c.name,
      comboType: c.comboType,
      role: c.position || "",
      wr: c.winRate,
      loseRate: c.loseRate,
      total: c.totalGames,
    }));

  const tierAiSummary = {
    scope: {
      perspective,
      opponent: perspective === "them" ? opponent : "",
      comboType,
      focusLabel,
      strictComboType,
      sortBy,
      dateType,
      selectedDates,
      selectedDateRange,
      selectedWeeks,
      selectedWeekRange,
      teamFilter,
      positionFilter,
      searchTerm,
    },
    totals: {
      games: filteredGames.length,
      homeGames: periodTeamFiltered.length,
      oneHeroCount: showCombos.filter((c) => c.comboType === "1-hero").length,
      combo2Count: showCombos.filter((c) => String(c.comboType).startsWith("2-heroes")).length,
      combo3Count: showCombos.filter((c) => String(c.comboType).startsWith("3-heroes")).length,
      availableCombos: showCombos.length,
      blockedHeroes: activeBlockSet.size,
      focusTopCount: focusTop.length,
    },
    focusInstructions: strictComboType
      ? `ตอบโดยยึดข้อมูลหมวด ${comboType} เป็นหลัก หลีกเลี่ยงการสรุปข้ามหมวด`
      : "ตอบจากภาพรวมทุกหมวดคอมโบ",
    focusTop,
    topOneHero: showCombos
      .filter((c) => c.comboType === "1-hero")
      .slice(0, 8)
      .map((c) => ({ hero: c.name, role: c.position || "", wr: c.winRate, total: c.totalGames })),
    topCombo2: showCombos
      .filter((c) => String(c.comboType).startsWith("2-heroes"))
      .slice(0, 8)
      .map((c) => ({ combo: c.name, wr: c.winRate, total: c.totalGames })),
    topCombo3: showCombos
      .filter((c) => String(c.comboType).startsWith("3-heroes"))
      .slice(0, 8)
      .map((c) => ({ combo: c.name, wr: c.winRate, total: c.totalGames })),
    topRisks: [...showCombos]
      .sort((a, b) => b.loseRate - a.loseRate)
      .slice(0, 8)
      .map((c) => ({
        team: perspective === "them" ? opponent : "FS",
        role: c.position || c.comboType || "",
        hero: c.name,
        deltaWinRate: -Number(c.loseRate || 0),
        wr: c.winRate,
      })),
    topStageReady: [...showCombos]
      .sort((a, b) => b.winRate - a.winRate)
      .slice(0, 8)
      .map((c) => ({
        team: perspective === "them" ? opponent : "FS",
        role: c.position || c.comboType || "",
        hero: c.name,
        deltaWinRate: Number(c.winRate || 0),
        wr: c.winRate,
      })),
    highPriorityActions: [],
    teamGapSummary: [],
  };

  /* -------- Charts -------- */
  const top5ChartItems = [...showCombos]
    .sort((a, b) =>
      chartMode === "winRate"
        ? b.winRate - a.winRate || b.totalGames - a.totalGames
        : b.totalGames - a.totalGames || b.winRate - a.winRate
    )
    .slice(0, 5);
  const chartIsWinRate = chartMode === "winRate";
  const top5ChartData = {
    labels: top5ChartItems.map((c) => c.name),
    datasets: [
      {
        label: chartIsWinRate ? "Win Rate (%)" : "Total Games",
        data: top5ChartItems.map((c) => (chartIsWinRate ? c.winRate : c.totalGames)),
        backgroundColor: theme.neon,
        borderColor: theme.main,
        borderWidth: 2
      }
    ]
  };

  /* -------- Save/Remove -------- */
  const saveCombo = (combo) => {
    if (!savedCombos.some((c) => c.name === combo.name && c.position === combo.position)) {
      const updated = [...savedCombos, combo];
      try { localStorage.setItem("savedCombos", JSON.stringify(updated)); } catch { }
      setSavedCombos(updated);
      setShowToast({ visible: true, message: `Saved "${combo.name}"` });
      setTimeout(() => setShowToast({ visible: false }), 1300);
    }
  };
  const removeCombo = (name, pos) => {
    const updated = savedCombos.filter((c) => !(c.name === name && c.position === pos));
    try { localStorage.setItem("savedCombos", JSON.stringify(updated)); } catch { }
    setSavedCombos(updated);
    setShowToast({ visible: true, message: `Removed "${name}"` });
    setTimeout(() => setShowToast({ visible: false }), 1000);
  };

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("savedCombos") || "[]");
      setSavedCombos(saved);
    } catch { }
  }, []);

  const handleAnalyzeTierByAI = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiError("");
    try {
      const analysis = await analyzeTeamByAI(tierAiSummary);
      setAiResult(analysis);
    } catch (err) {
      setAiError(err?.message || "ไม่สามารถวิเคราะห์ TierData ด้วย AI Assistant ได้");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAskTierCoach = async (inputQuestion) => {
    if (coachLoading) return;
    const q = String(inputQuestion || coachQuestion || "").trim();
    if (!q) return;
    const scopedQuestion = strictComboType
      ? `[Focus: ${comboType}] ${q} (ตอบเฉพาะหมวดนี้)`
      : q;
    setCoachLoading(true);
    setCoachError("");
    try {
      const next = await askCoachByAI(scopedQuestion, tierAiSummary);
      setCoachResult(next);
      setCoachQuestion("");
    } catch (err) {
      setCoachError(err?.message || "ไม่สามารถถาม AI Assistant ได้");
    } finally {
      setCoachLoading(false);
    }
  };

  // รีเซ็ตหน้า “เฉพาะเหตุใหญ่”
  useEffect(() => { setCurrentPage(1); }, [perspective, opponent, comboType]);

  // ถ้าจำนวนหน้าเปลี่ยน ให้หด currentPage ไม่ให้เกินหน้าสุดท้าย (ป้องกันวูบ)
  const totalPages = Math.max(1, Math.ceil(showCombos.length / itemsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages]); // ไม่ต้องใส่ currentPage ใน deps

  /* -------- Tabs scroller controls (NEW) -------- */
  const scrollerRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  function updateArrowState() {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrowState();

    const onScroll = () => updateArrowState();
    el.addEventListener("scroll", onScroll, { passive: true });

    const ro = new ResizeObserver(updateArrowState);
    ro.observe(el);
    window.addEventListener("resize", updateArrowState);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
      window.removeEventListener("resize", updateArrowState);
    };
  }, []);

  const scrollByX = (dx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  const onWheelHorizontal = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  };

  /* -------- Render -------- */
  return (
    <Bg>
      {(!effectiveComboStats || !games) ? (
        <div style={{
          color: theme.text, padding: 40, fontWeight: 900,
          fontSize: 26, textAlign: "center",
          background: theme.bg, minHeight: "100vh"
        }}>
          กำลังโหลดข้อมูล...
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center", fontWeight: 900, fontSize: "1.2rem", margin: "10px 0" }}>
            {contextTitle
              ? contextTitle
              : (perspective === "us" ? "มุมมองเรา" : `มุมมองเขา (${opponent})`)}
          </div>

          {/* Tabs (Scrollable with arrows) */}
          <TabsWrap>
            <TabsViewport>
              <TabsScroller ref={scrollerRef} onWheel={onWheelHorizontal}>
                <TabBtn active={comboType === "all"} onClick={() => setComboType("all")}>All</TabBtn>
                <TabBtn active={comboType === "1-hero"} onClick={() => setComboType("1-hero")}>Hero</TabBtn>
                <TabBtn active={comboType === "2-heroes (MID+SUP)"} onClick={() => setComboType("2-heroes (MID+SUP)")}>2-Heroes (MID+SUP)</TabBtn>
                <TabBtn active={comboType === "2-heroes (ADL+SUP)"} onClick={() => setComboType("2-heroes (ADL+SUP)")}>2-Heroes (ADL+SUP)</TabBtn>
                <TabBtn active={comboType === "2-heroes (DSL+Jungle)"} onClick={() => setComboType("2-heroes (DSL+Jungle)")}>2-Heroes (DSL+Jungle)</TabBtn>
                <TabBtn active={comboType === "2-heroes (JUG+SUP)"} onClick={() => setComboType("2-heroes (JUG+SUP)")}>2-Heroes (JUG+SUP)</TabBtn>
                <TabBtn active={comboType === "2-heroes (JUG+MID)"} onClick={() => setComboType("2-heroes (JUG+MID)")}>2-Heroes (JUG+MID)</TabBtn>
                {/* NEW: 3-heroes tabs */}
                <TabBtn active={comboType === "3-heroes (JUG+MID+SUP)"} onClick={() => setComboType("3-heroes (JUG+MID+SUP)")}>3-Heroes (JUG+MID+SUP)</TabBtn>
                <TabBtn active={comboType === "3-heroes (JUG+ADL+SUP)"} onClick={() => setComboType("3-heroes (JUG+ADL+SUP)")}>3-Heroes (JUG+ADL+SUP)</TabBtn>
                <TabBtn active={comboType === "3-heroes (MID+ADL+SUP)"} onClick={() => setComboType("3-heroes (MID+ADL+SUP)")}>3-Heroes (MID+ADL+SUP)</TabBtn>
                <TabBtn active={comboType === "5-heroes"} onClick={() => setComboType("5-heroes")}>5-Heroes</TabBtn>
              </TabsScroller>
            </TabsViewport>

            <ArrowLeft
              show={canScrollLeft}
              onClick={() => scrollByX(-Math.round((scrollerRef.current?.clientWidth || 320) * 0.8))}
              aria-label="Scroll tabs left"
            >
              <FiChevronLeft />
            </ArrowLeft>
            <ArrowRight
              show={canScrollRight}
              onClick={() => scrollByX(Math.round((scrollerRef.current?.clientWidth || 320) * 0.8))}
              aria-label="Scroll tabs right"
            >
              <FiChevronRight />
            </ArrowRight>

            <EdgeFade left show={canScrollLeft} />
            <EdgeFade show={canScrollRight} />
          </TabsWrap>

          {/* Filters */}
          <FilterBar>
            {/* Perspective */}
            <FilterGroup>
              <FilterLabel>มุมมอง</FilterLabel>
              <BtnGroup>
                <FilterBtn active={perspective === "us"} onClick={() => setPerspective("us")}>เรา</FilterBtn>
                <FilterBtn active={perspective === "them"} onClick={() => setPerspective("them")}>เขา</FilterBtn>
              </BtnGroup>
            </FilterGroup>

            {perspective === "them" && !hideOpponentSelect && (
              <FilterGroup style={{ minWidth: 160 }}>
                <FilterLabel>ทีมคู่แข่ง</FilterLabel>
                <select
                  value={opponent}
                  onChange={(e) => {
                    const val = e.target.value;
                    setOpponent(val);
                    if (typeof onOpponentChange === 'function') onOpponentChange(val);
                  }}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: theme.card,
                    color: theme.text,
                    fontWeight: 900,
                    border: `1px solid ${theme.accent}`
                  }}
                >
                  {opponentOptions.length === 0 && <option value="">No opponent</option>}
                  {opponentOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {loadingOpp && (
                  <div style={{ marginTop: 6, color: theme.secondary, fontWeight: 700, fontSize: "0.9rem" }}>
                    กำลังโหลดข้อมูลทีม {opponent}...
                  </div>
                )}
              </FilterGroup>
            )}

            {/* Sort By */}
            <FilterGroup>
              <FilterLabel>Sort By</FilterLabel>
              <BtnGroup>
                <FilterBtn active={sortBy === "totalGames"} onClick={() => setSortBy("totalGames")}>Total Games</FilterBtn>
                <FilterBtn active={sortBy === "mostWins"} onClick={() => setSortBy("mostWins")}>Most Wins</FilterBtn>
                <FilterBtn active={sortBy === "mostLosses"} onClick={() => setSortBy("mostLosses")}>Most Losses</FilterBtn>
                <FilterBtn active={sortBy === "winRate"} onClick={() => setSortBy("winRate")}>Win Rate</FilterBtn>
                <FilterBtn active={sortBy === "loseRate"} onClick={() => setSortBy("loseRate")}>Lose Rate</FilterBtn>
              </BtnGroup>
            </FilterGroup>

            {/* Picks/Bans */}
            <FilterGroup style={{ minWidth: 260 }}>
              <FilterLabel>เราเลือกแล้ว</FilterLabel>
              <Select
                options={heroOptionsForSelect}
                isMulti
                placeholder="เลือกฮีโร่ของเรา..."
                value={myPicks.map(h => ({ value: h, label: h }))}
                onChange={(vals) => setMyPicks(vals.map(v => v.value))}
                styles={{ control: (s) => ({ ...s, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }), menu: (s) => ({ ...s, background: theme.card, color: theme.text }) }}
              />
            </FilterGroup>

            <FilterGroup style={{ minWidth: 260 }}>
              <FilterLabel>เขาเลือกแล้ว</FilterLabel>
              <Select
                options={heroOptionsForSelect}
                isMulti
                placeholder="เลือกฮีโร่ฝั่งตรงข้าม..."
                value={oppPicks.map(h => ({ value: h, label: h }))}
                onChange={(vals) => setOppPicks(vals.map(v => v.value))}
                styles={{ control: (s) => ({ ...s, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }), menu: (s) => ({ ...s, background: theme.card, color: theme.text }) }}
              />
            </FilterGroup>

            <FilterGroup style={{ minWidth: 240 }}>
              <FilterLabel>แบน</FilterLabel>
              <Select
                options={heroOptionsForSelect}
                isMulti
                placeholder="ฮีโร่ที่โดนแบน..."
                value={banList.map(h => ({ value: h, label: h }))}
                onChange={(vals) => setBanList(vals.map(v => v.value))}
                styles={{ control: (s) => ({ ...s, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }), menu: (s) => ({ ...s, background: theme.card, color: theme.text }) }}
              />
            </FilterGroup>

            {/* Period */}
            <FilterGroup>
              <FilterLabel>ชนิดช่วง</FilterLabel>
              <BtnGroup>
                <FilterBtn
                  active={dateType === "day"}
                  onClick={() => {
                    setDateType("day");
                    setSelectedWeeks([]);
                    setSelectedWeekRange({ from: "", to: "" });
                  }}
                >
                  วันที่
                </FilterBtn>
                <FilterBtn
                  active={dateType === "week"}
                  onClick={() => {
                    setDateType("week");
                    setSelectedDates([]);
                    setSelectedDateRange({ from: "", to: "" });
                  }}
                >
                  สัปดาห์
                </FilterBtn>
              </BtnGroup>
            </FilterGroup>

            {/* Date / Week Selectors */}
            {dateType === "day" ? (
              <FilterGroup style={{ minWidth: 320, maxWidth: 400 }}>
                <FilterLabel>เลือกวัน (Multi หรือ ช่วง)</FilterLabel>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      options={dateOptionsForSelect}
                      isMulti
                      placeholder="เลือกหลายวัน..."
                      value={selectedDates.map((d) => ({ value: d, label: d }))}
                      onChange={(vals) => {
                        setSelectedDates(vals.map((v) => v.value));
                        setSelectedDateRange({ from: "", to: "" });
                      }}
                      styles={{
                        control: (s) => ({ ...s, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }),
                        menu: (s) => ({ ...s, background: theme.card, color: theme.text }),
                        multiValue: (s) => ({ ...s, background: theme.accent, color: theme.text }),
                        input: (s) => ({ ...s, color: theme.text })
                      }}
                    />
                  </div>
                  <span style={{ color: theme.accent, fontWeight: 900 }}>หรือ</span>
                  <Select
                    options={dateOptionsForSelect}
                    placeholder="จาก"
                    isClearable
                    value={selectedDateRange.from ? { value: selectedDateRange.from, label: selectedDateRange.from } : null}
                    onChange={(val) => {
                      setSelectedDateRange((r) => ({ ...r, from: val?.value || "" }));
                      setSelectedDates([]);
                    }}
                    styles={{
                      control: (s) => ({ ...s, minWidth: 80, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }),
                      menu: (s) => ({ ...s, background: theme.card, color: theme.text }),
                      input: (s) => ({ ...s, color: theme.text })
                    }}
                  />
                  <Select
                    options={dateOptionsForSelect}
                    placeholder="ถึง"
                    isClearable
                    value={selectedDateRange.to ? { value: selectedDateRange.to, label: selectedDateRange.to } : null}
                    onChange={(val) => {
                      setSelectedDateRange((r) => ({ ...r, to: val?.value || "" }));
                      setSelectedDates([]);
                    }}
                    styles={{
                      control: (s) => ({ ...s, minWidth: 80, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }),
                      menu: (s) => ({ ...s, background: theme.card, color: theme.text }),
                      input: (s) => ({ ...s, color: theme.text })
                    }}
                  />
                </div>
              </FilterGroup>
            ) : (
              <FilterGroup style={{ minWidth: 280, maxWidth: 340 }}>
                <FilterLabel>เลือกสัปดาห์ (Multi หรือ ช่วง)</FilterLabel>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      options={weekOptionsForSelect}
                      isMulti
                      placeholder="เลือกหลายสัปดาห์..."
                      value={selectedWeeks.map((w) => ({ value: w, label: w }))}
                      onChange={(vals) => {
                        setSelectedWeeks(vals.map((v) => v.value));
                        setSelectedWeekRange({ from: "", to: "" });
                      }}
                      styles={{
                        control: (s) => ({ ...s, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }),
                        menu: (s) => ({ ...s, background: theme.card, color: theme.text }),
                        multiValue: (s) => ({ ...s, background: theme.accent, color: "#191c2b" }),
                        input: (s) => ({ ...s, color: theme.text })
                      }}
                    />
                  </div>
                  <span style={{ color: theme.accent, fontWeight: 900 }}>หรือ</span>
                  <Select
                    options={weekOptionsForSelect}
                    placeholder="จาก"
                    isClearable
                    value={selectedWeekRange.from ? { value: selectedWeekRange.from, label: selectedWeekRange.from } : null}
                    onChange={(val) => {
                      setSelectedWeekRange((r) => ({ ...r, from: val?.value || "" }));
                      setSelectedWeeks([]);
                    }}
                    styles={{
                      control: (s) => ({ ...s, minWidth: 80, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }),
                      menu: (s) => ({ ...s, background: theme.card, color: theme.text }),
                      input: (s) => ({ ...s, color: theme.text })
                    }}
                  />
                  <Select
                    options={weekOptionsForSelect}
                    placeholder="ถึง"
                    isClearable
                    value={selectedWeekRange.to ? { value: selectedWeekRange.to, label: selectedWeekRange.to } : null}
                    onChange={(val) => {
                      setSelectedWeekRange((r) => ({ ...r, to: val?.value || "" }));
                      setSelectedWeeks([]);
                    }}
                    styles={{
                      control: (s) => ({ ...s, minWidth: 80, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }),
                      menu: (s) => ({ ...s, background: theme.card, color: theme.text }),
                      input: (s) => ({ ...s, color: theme.text })
                    }}
                  />
                </div>
              </FilterGroup>
            )}

            {/* Team Filter */}
            <FilterGroup style={{ minWidth: 200 }}>
              <FilterLabel>ทีม</FilterLabel>
              <Select
                options={teamOptionsForSelect}
                isMulti
                placeholder="เลือกหลายทีม..."
                value={teamFilter.map((t) => ({ value: t, label: t }))}
                onChange={(vals) => setTeamFilter(vals.map((v) => v.value))}
                styles={{
                  control: (s) => ({ ...s, minHeight: 40, background: theme.card, color: theme.text, fontWeight: 900 }),
                  menu: (s) => ({ ...s, background: theme.card, color: theme.text }),
                  multiValue: (s) => ({ ...s, background: theme.accent, color: "#191c2b" }),
                  input: (s) => ({ ...s, color: theme.text })
                }}
              />
            </FilterGroup>

            {/* Position Filter */}
            {comboType === "1-hero" && (
              <FilterGroup style={{ minWidth: 150 }}>
                <FilterLabel>ตำแหน่ง</FilterLabel>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {positionOptions.map((pos) => (
                    <FilterBtn
                      key={pos}
                      active={positionFilter.includes(pos)}
                      onClick={() =>
                        setPositionFilter((ps) =>
                          ps.includes(pos) ? ps.filter((x) => x !== pos) : [...ps, pos]
                        )
                      }
                      style={{ fontSize: "0.99rem", padding: "8px 12px", minWidth: 44 }}
                    >
                      {pos}
                    </FilterBtn>
                  ))}
                </div>
              </FilterGroup>
            )}

            {/* Search */}
            <FilterGroup style={{ flex: 1, minWidth: 220 }}>
              <FilterLabel>Search</FilterLabel>
              <SearchWrap>
                <FiSearch style={{ fontSize: 22, color: theme.accent, marginRight: 3 }} />
                <SearchInput
                  placeholder="ค้นหาฮีโร่ หรือ combo..."
                  value={rawSearch}
                  onChange={(e) => setRawSearch(e.target.value)}
                />
              </SearchWrap>
            </FilterGroup>
          </FilterBar>

          {/* Summary line */}
          <div style={{ textAlign: "center", margin: "6px 0 12px", fontWeight: 900 }}>
            เราเลือกไป {myPicks.length} ตัว / เขาเลือกไป {oppPicks.length} ตัว / แบน {banList.length} ตัว —
            คอมโบที่ <span style={{ color: theme.accent }}>{perspective === "us" ? "เรา" : "เขา"}</span> ยังใช้ได้:{" "}
            <span style={{ color: theme.main }}>{showCombos.length}</span> รายการ
          </div>
          {useScopedUsFromGames && (
            <div style={{ textAlign: "center", margin: "0 0 12px", fontWeight: 800, color: theme.secondary }}>
              โหมด Team Scope: คำนวณจากเกมที่ถูกฟิลเตอร์ (ไม่ใช้ค่า aggregate รวมทุกทีม)
            </div>
          )}

          {/* AI Tier Brief */}
          <div
            style={{
              width: "90%",
              maxWidth: 1120,
              margin: "0 auto 18px",
              background: theme.cardGlass,
              border: "1px solid rgba(255,16,38,0.28)",
              borderRadius: 18,
              padding: 14,
              boxShadow: "0 0 20px rgba(255,16,38,0.12)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ color: theme.main, fontWeight: 900, fontSize: "1.12rem" }}>AI Assistant</div>
              <div style={{ color: theme.secondary, fontWeight: 800, fontSize: "0.9rem" }}>
                scope: {perspective === "us" ? "เรา" : `เขา (${opponent})`} • {showCombos.length} combos • {strictComboType ? comboType : "all"}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              <ShineBtn onClick={handleAnalyzeTierByAI} disabled={aiLoading} style={{ opacity: aiLoading ? 0.7 : 1 }}>
                {aiLoading ? "กำลังวิเคราะห์..." : "วิเคราะห์ TierData ด้วย AI Assistant"}
              </ShineBtn>
              {TIER_AI_QUICK_PROMPTS.map((q) => (
                <button
                  key={q}
                  onClick={() => handleAskTierCoach(q)}
                  disabled={coachLoading}
                  style={{
                    background: theme.tintMd,
                    color: theme.main,
                    border: "1px solid rgba(255,16,38,0.35)",
                    borderRadius: 10,
                    padding: "7px 11px",
                    fontWeight: 800,
                    cursor: coachLoading ? "not-allowed" : "pointer",
                    opacity: coachLoading ? 0.65 : 1,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>

            {aiError && <div style={{ marginTop: 8, color: theme.danger, fontWeight: 900 }}>{aiError}</div>}
            {aiResult && (
              <div style={{ marginTop: 10, color: theme.white }}>
                <div style={{ color: theme.accent, fontWeight: 900, marginBottom: 6 }}>AI Assistant Summary</div>
                <div style={{ fontWeight: 700 }}>{aiResult?.summary || "-"}</div>
                <div style={{ marginTop: 6, color: theme.secondary }}>
                  {(aiResult?.nextActions || []).slice(0, 3).map((item, idx) => (
                    <div key={`tier-ai-next-${idx}`}>- {item}</div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <div style={{ color: theme.main, fontWeight: 900, marginBottom: 6 }}>AI Assistant</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  value={coachQuestion}
                  onChange={(e) => setCoachQuestion(e.target.value)}
                  placeholder="ถาม AI Assistant เช่น ควรเน้นคอมโบไหนในสัปดาห์นี้"
                  style={{
                    flex: 1,
                    minWidth: 260,
                    background: theme.cardGlass,
                    border: "1px solid rgba(255,16,38,0.3)",
                    borderRadius: 10,
                    color: theme.text,
                    padding: "10px 12px",
                    fontWeight: 800,
                    outline: "none",
                  }}
                />
                <ShineBtn onClick={() => handleAskTierCoach()} disabled={coachLoading} style={{ opacity: coachLoading ? 0.7 : 1 }}>
                  {coachLoading ? "กำลังตอบ..." : "ถาม AI Assistant"}
                </ShineBtn>
              </div>
              {coachError && <div style={{ marginTop: 8, color: theme.danger, fontWeight: 900 }}>{coachError}</div>}
              {coachResult?.answer && (
                <div style={{ marginTop: 10, color: theme.white }}>
                  <div style={{ color: theme.accent, fontWeight: 900, marginBottom: 4 }}>คำตอบ</div>
                  <div style={{ fontWeight: 700 }}>{coachResult.answer}</div>
                </div>
              )}
            </div>
          </div>

          {/* Charts */}
          <div
            style={{
              display: "flex",
              gap: 48,
              flexWrap: "wrap",
              justifyContent: "center",
              margin: "0 auto 38px",
              maxWidth: 1280
            }}
          >
            <div
              style={{
                width: 340,
                background: theme.card,
                borderRadius: 16,
                padding: "17px 14px 24px",
                boxShadow: "0 1px 18px #3df2b218"
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 9,
                  flexWrap: "wrap"
                }}
              >
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: "1.13rem",
                    color: theme.accent,
                  }}
                >
                  {chartIsWinRate ? "Top 5 by Win Rate" : "Top 5 by Total Games"}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <FilterBtn
                    active={chartIsWinRate}
                    onClick={() => setChartMode("winRate")}
                    style={{ fontSize: "0.92rem", padding: "8px 12px" }}
                  >
                    Win Rate
                  </FilterBtn>
                  <FilterBtn
                    active={!chartIsWinRate}
                    onClick={() => setChartMode("totalGames")}
                    style={{ fontSize: "0.92rem", padding: "8px 12px" }}
                  >
                    Total Games
                  </FilterBtn>
                </div>
              </div>
              <Bar
                data={top5ChartData}
                options={{
                  plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const item = top5ChartItems[context.dataIndex];
                          if (!item) return "";
                          return chartIsWinRate
                            ? `Win Rate: ${item.winRate}%`
                            : `Total Games: ${item.totalGames}`;
                        },
                        afterLabel: (context) => {
                          const item = top5ChartItems[context.dataIndex];
                          if (!item) return "";
                          return chartIsWinRate
                            ? `Total Games: ${item.totalGames}`
                            : `Win Rate: ${item.winRate}%`;
                        }
                      }
                    }
                  },
                  responsive: true,
                  scales: {
                    y: { beginAtZero: true, ticks: { color: theme.neon } },
                    x: chartIsWinRate
                      ? {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          color: theme.neon,
                          callback: (value) => `${value}%`
                        }
                      }
                      : {
                        beginAtZero: true,
                        ticks: { color: theme.neon }
                      }
                  },
                  indexAxis: "y",
                  elements: { bar: { borderRadius: 10 } }
                }}
              />
            </div>
          </div>

          {/* Grid Area */}
          <GlobalPulse />
          {loadingOpp ? (
            <div
              style={{
                minHeight: 360,
                display: "grid",
                placeItems: "center",
                background: theme.bg,
                borderRadius: 18,
                boxShadow: "0 2px 16px #3df2b233",
                margin: "40px auto",
                maxWidth: 560,
                fontWeight: 900
              }}
            >
              กำลังโหลดข้อมูลทีม {opponent}...
            </div>
          ) : (
            <>
              {isDateRangeIncomplete || isWeekRangeIncomplete ? (
                <div
                  style={{
                    background: theme.bg,
                    borderRadius: 18,
                    boxShadow: "0 2px 16px #ef476f44",
                    margin: "40px auto",
                    maxWidth: 500,
                    padding: "40px 18px",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: "1.32rem",
                    color: theme.warn,
                    border: `2.5px solid ${theme.warn}`
                  }}
                >
                  กรุณาใส่ข้อมูลช่อง <span style={{ color: theme.accent }}>ถึง</span> ให้ครบ
                </div>
              ) : (
                <ComboGrid>
                  {(!showCombos || showCombos.length === 0) ? (
                    Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={`sk-${i}`} />)
                  ) : (
                    currentCombos.map((combo, i) => (
                      <Tilt
                        key={`${combo.name}|${combo.position || ""}|${i}`}
                        glareEnable
                        glareMaxOpacity={0.18}
                        glarePosition="all"
                      >
                        <ComboCard onClick={() => setSelectedCombo(combo)}>
                          <HeroRow>
                            {asArray(combo.images).map((img, idx) => {
                              const cardRoles = combo.comboType === "1-hero"
                                ? [combo.position]
                                : rolesFromComboLabel(combo.comboType);
                              const byName =
                                typeof heroImageOf === 'function'
                                  ? (heroImageOf(combo.characters?.[idx], cardRoles[idx]) || img)
                                  : img;
                              return (
                                <NeonHero key={idx}>
                                  {renderHeroImage(byName, combo.characters?.[idx])}
                                </NeonHero>
                              );
                            })}
                          </HeroRow>
                          <ComboTitle>
                            {combo.name}
                            {combo.position && (
                              <span style={{ color: theme.accent }}>
                                ({combo.position})
                              </span>
                            )}
                          </ComboTitle>
                          <ComboDetail>
                            <span style={{ color: theme.success }}>ชนะ {combo.wins}</span>
                            <span style={{ color: theme.main }}>/ {combo.totalGames} เกม</span>
                            <span style={{ color: theme.danger }}>/ แพ้ {combo.losses}</span>
                            <span
                              style={{
                                marginLeft: 8,
                                color: sortBy === "loseRate" ? theme.danger : theme.success
                              }}
                            >
                              {sortBy === "loseRate"
                                ? `${combo.loseRate}% แพ้`
                                : `${combo.winRate}% ชนะ`}
                            </span>
                          </ComboDetail>
                          <ActionRow>
                            {!savedCombos.some(
                              (c) => c.name === combo.name && c.position === combo.position
                            ) ? (
                              <ShineBtn
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveCombo(combo);
                                }}
                              >
                                <FiStar /> Save
                              </ShineBtn>
                            ) : (
                              <ShineBtn
                                style={{ background: theme.danger }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeCombo(combo.name, combo.position);
                                }}
                              >
                                <FiTrash2 /> Remove
                              </ShineBtn>
                            )}
                          </ActionRow>
                        </ComboCard>
                      </Tilt>
                    ))
                  )}
                </ComboGrid>
              )}
            </>
          )}

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              gap: 22,
              alignItems: "center",
              justifyContent: "center",
              margin: "38px 0 42px"
            }}
          >
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                background: theme.card,
                color: theme.main,
                border: "none",
                borderRadius: 11,
                fontSize: 22,
                padding: "7px 15px",
                cursor: "pointer",
                fontWeight: 900
              }}
            >
              <FiChevronLeft />
            </button>
            <span style={{ fontWeight: 900, color: theme.main }}>Page {currentPage}</span>
            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage * itemsPerPage >= showCombos.length}
              style={{
                background: theme.card,
                color: theme.main,
                border: "none",
                borderRadius: 11,
                fontSize: 22,
                padding: "7px 15px",
                cursor: "pointer",
                fontWeight: 900
              }}
            >
              <FiChevronRight />
            </button>
          </div>

          {/* Modal */}
          {selectedCombo && (
            <ModalOverlay onClick={() => setSelectedCombo(null)}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <CloseBtn onClick={() => setSelectedCombo(null)}>×</CloseBtn>
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 8,
                      marginBottom: 16
                    }}
                  >
                    {selectedCombo.images.map((img, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: 100,
                          height: 100,
                          borderRadius: 50,
                          overflow: "hidden",
                          boxShadow: `0 0 12px ${theme.neon}`
                        }}
                      >
                        {renderHeroImage(img, selectedCombo.characters[idx])}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: 900,
                      color: theme.main,
                      marginBottom: 12
                    }}
                  >
                    {selectedCombo.name}
                    {selectedCombo.position && (
                      <span style={{ fontSize: "1.2rem", color: theme.accent, marginLeft: 6 }}>
                        ({selectedCombo.position})
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 20,
                      flexWrap: "wrap",
                      marginBottom: 16,
                      fontSize: "1.1rem",
                      fontWeight: 700
                    }}
                  >
                    <div><b style={{ color: theme.success }}>{selectedCombo.winRate}%</b> Win Rate</div>
                    <div><b style={{ color: theme.danger }}>{selectedCombo.loseRate}%</b> Lose Rate</div>
                    <div><b style={{ color: theme.warn }}>{selectedCombo.totalGames}</b> Total Games</div>
                  </div>
                  <div style={{ fontSize: "1rem", color: theme.white }}>
                    <div>Wins: <b style={{ color: theme.success }}>{selectedCombo.wins}</b></div>
                    <div>Losses: <b style={{ color: theme.danger }}>{selectedCombo.losses}</b></div>
                    {selectedCombo.comboType === "1-hero" && (
                      <>
                        <div>Avg KDA: <b style={{ color: theme.main }}>{fmtNum(selectedCombo.avgKda, 2)}</b></div>
                        <div>Avg Participation: <b style={{ color: theme.main }}>{fmtPercent(selectedCombo.avgParticipation, 1)}</b></div>
                        <div>Avg GPM: <b style={{ color: theme.main }}>{fmtInt(selectedCombo.avgGpm)}</b></div>
                        <div>Avg DMG/Min: <b style={{ color: theme.main }}>{fmtInt(selectedCombo.avgDmgPerMin)}</b></div>
                        <div>Pick Rate: <b style={{ color: theme.main }}>{fmtPercent(selectedCombo.pickRate, 1)}</b></div>
                      </>
                    )}
                  </div>
                  {selectedCombo.comboType === "1-hero" && (
                    <div
                      style={{
                        marginTop: 16,
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 10,
                        textAlign: "left",
                        width: "100%",
                        maxWidth: 460,
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                    >
                      <div>Avg KDA: <b style={{ color: theme.gold }}>{formatMetric(selectedCombo.avgKDA, 2)}</b></div>
                      <div>Avg Participation: <b style={{ color: theme.blue }}>{formatMetric(selectedCombo.avgParticipation, 1)}%</b></div>
                      <div>Avg GPM: <b style={{ color: theme.accent }}>{formatMetric(selectedCombo.avgGPM, 0)}</b></div>
                      <div>Avg DMG/Min: <b style={{ color: theme.warn }}>{formatMetric(selectedCombo.avgDmgPerMin, 0)}</b></div>
                      <div>Pick Rate: <b style={{ color: theme.success }}>{formatMetric(selectedCombo.pickRate, 1)}%</b></div>
                    </div>
                  )}

                  <HeroMatchupPanel
                    games={matchupGames}
                    combo={selectedCombo}
                    side={perspective === "them" ? "them" : "us"}
                    opponentLabel={opponent}
                    heroImageOf={heroImageOf}
                    scope={matchupScope}
                    onScopeChange={excelFile ? setMatchupScope : undefined}
                    scopeCounts={{
                      current: fallbackStatGames.length,
                      season: seasonStatGames.length,
                    }}
                    scopeLoading={seasonLogLoading}
                    seasonGames={seasonMatchupGames}
                    scouted={scoutedGames}
                    source={matchupSource}
                    onSourceChange={setMatchupSource}
                  />
                </div>
              </ModalContent>
            </ModalOverlay>
          )}

          {/* Toast */}
          {showToast.visible && <Toast>{showToast.message}</Toast>}

          {/* Scroll to top */}
          <ScrollTopBtn onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <FiChevronUp />
          </ScrollTopBtn>
        </>
      )}
    </Bg>
  );
}
